import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import MoveBookingDialog from '@/components/MoveBookingDialog';
import { isDepositUnpaid, formatDepositAmount, confirmUnpaidDepositArrival, confirmDepositRefundOnCancel, describeCancelDepositOutcome } from '@/lib/deposit';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Clock, 
  Users, 
  Phone,
  ChevronLeft,
  ChevronRight,
  Plus,
  LogOut,
  Loader2,
  UserCheck,
  UserX,
  Check,
  CheckCircle,
  XCircle,
  AlertTriangle,
  LayoutGrid,
  Edit2,
  Table2,
  Ban,
  Unlock,
  Mail,
  CreditCard,
  Copy,
  MessageSquare,
  ExternalLink,
  UserPlus,
  Sparkles,
  Delete,
  CalendarClock,
  Lock,
  RefreshCw
} from 'lucide-react';
import { format, addDays, parse } from 'date-fns';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CURRENCY_SYMBOLS = { gbp: '£', usd: '$', eur: '€' };

const depositMethodTag = (booking) =>
  booking?.deposit_payment_method === 'manual_phone' ? ' (Phone)' : '';

/** Label for assigned tables on booking cards (uses API enrichment when present). */
function getAssignedTablesText(booking, tablesList = []) {
  const details = booking.assigned_table_details;
  if (Array.isArray(details) && details.length > 0) {
    return details.map((d) => d?.name || d?.id || '?').join(' · ');
  }
  const ids = booking.assigned_tables;
  if (!Array.isArray(ids) || ids.length === 0) return null;
  return ids
    .map((tid) => tablesList.find((t) => t.id === tid)?.name || `ID ${String(tid).slice(0, 8)}…`)
    .join(' · ');
}

const STATUS_OPTIONS = [
  { value: 'confirmed', label: 'Confirmed', color: 'bg-blue-100 text-blue-800' },
  { value: 'arrived', label: 'Arrived', color: 'bg-amber-100 text-amber-800' },
  { value: 'seated', label: 'Seated', color: 'bg-emerald-100 text-emerald-800' },
  { value: 'completed', label: 'Completed', color: 'bg-slate-100 text-slate-800' },
  { value: 'no-show', label: 'No Show', color: 'bg-gray-100 text-gray-800' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-800' }
];

export default function TabletView() {
  const navigate = useNavigate();
  const { restaurant, logout, switchUser, user } = useAuth();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const [tables, setTables] = useState([]);
  const [calendarCounts, setCalendarCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCalendar, setShowCalendar] = useState(true);
  const [viewMode, setViewMode] = useState('timeline'); // 'timeline' or 'tables'
  const [selectedZone, setSelectedZone] = useState('all'); // Zone filter
  const [selectedPeriod, setSelectedPeriod] = useState('all'); // Period filter (Lunch / Evening / All)
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedTableForAssign, setSelectedTableForAssign] = useState(null);
  
  // Walk-in dialog
  const [walkinDialogOpen, setWalkinDialogOpen] = useState(false);
  /** When adding a walk-in from table view: optionally smart-assign or prefer the open table */
  const [walkinPlacement, setWalkinPlacement] = useState({
    smartPlace: true,
    preferTableId: null
  });
  const [walkinData, setWalkinData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    party_size: '2',
    allergies: ''
  });
  const [submitting, setSubmitting] = useState(false);
  
  // Phone booking dialog
  const [phoneDialogOpen, setPhoneDialogOpen] = useState(false);
  const [phoneBookingStep, setPhoneBookingStep] = useState(1); // 1: phone lookup, 2: details, 3: time selection, 4: deposit required
  const [phoneData, setPhoneData] = useState({
    customer_phone: '',
    customer_name: '',
    customer_email: '',
    party_size: '2',
    allergies: '',
    special_requests: '',
    booking_date: null,
    booking_time: ''
  });
  const [customerFound, setCustomerFound] = useState(false);
  const [searchingCustomer, setSearchingCustomer] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [phoneDepositInfo, setPhoneDepositInfo] = useState(null);
  const [phoneWaiveDeposit, setPhoneWaiveDeposit] = useState(false);
  const [sendingDepositEmail, setSendingDepositEmail] = useState(false);
  const phoneSubmitLockRef = useRef(false);
  const [depositLinkDialog, setDepositLinkDialog] = useState({ open: false, url: '', customerPhone: '', customerEmail: '' });
  
  // Edit booking dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [moveTarget, setMoveTarget] = useState(null);
  const [editingBooking, setEditingBooking] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [markingDepositPaid, setMarkingDepositPaid] = useState(false);
  const [phoneDepositAmount, setPhoneDepositAmount] = useState('');
  const [editTimeUnlocked, setEditTimeUnlocked] = useState(false);
  const [tableAvailability, setTableAvailability] = useState({});
  
  // Time slot action dialog (for clicking on timeline)
  const [timeSlotDialogOpen, setTimeSlotDialogOpen] = useState(false);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [blockedTimes, setBlockedTimes] = useState([]); // Array of blocked slot objects from DB
  const [slotsToBlock, setSlotsToBlock] = useState([]); // Selected slot numbers to block
  const [blockingSlots, setBlockingSlots] = useState(false);
  
  // Quick booking dialog (for adding booking from timeline click)
  const [quickBookingDialogOpen, setQuickBookingDialogOpen] = useState(false);
  const [quickBookingData, setQuickBookingData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    party_size: '2',
    allergies: '',
    booking_time: '',
    assigned_tables: [],
    /** Staff-only holds: shown on tablet, excluded from public booking site capacity */
    is_staff_hold: false
  });

  useEffect(() => {
    fetchCalendarData();
  }, []);

  useEffect(() => {
    fetchDayBookings();
    setSelectedPeriod('all'); // Reset period filter when date changes
  }, [selectedDate]);

  // Auto-scroll to current time on mount/date change (only for today)
  useEffect(() => {
    const scrollToCurrentTime = () => {
      const today = format(new Date(), 'yyyy-MM-dd');
      const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');
      
      // Only auto-scroll if viewing today
      if (today !== selectedDateStr) return;
      
      const now = new Date();
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();
      const currentTime = `${String(currentHour).padStart(2, '0')}:${String(Math.floor(currentMinute / 30) * 30).padStart(2, '0')}`;
      
      // Wait for DOM to render
      setTimeout(() => {
        // Try to find the time slot element
        const timeElement = document.querySelector(`[data-time="${currentTime}"]`);
        if (timeElement) {
          timeElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'center'
          });
        }
      }, 500);
    };
    
    scrollToCurrentTime();
  }, [selectedDate, viewMode]);

  const fetchCalendarData = async () => {
    try {
      const response = await axios.get(`${API}/tablet/calendar`);
      setCalendarCounts(response.data.booking_counts);
    } catch (error) {
      console.error('Failed to fetch calendar data:', error);
    }
  };

  const fetchDayBookings = async () => {
    setLoading(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const [bookingsRes, blockedRes] = await Promise.all([
        axios.get(`${API}/tablet/bookings?date=${dateStr}`),
        axios.get(`${API}/tablet/blocked-slots?date=${dateStr}`)
      ]);
      setBookings(bookingsRes.data.bookings);
      setTables(bookingsRes.data.tables || []);
      setBlockedTimes(blockedRes.data.blocked_slots || []);
    } catch (error) {
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchUser = async () => {
    try {
      await switchUser();
      navigate('/admin/pin-login');
    } catch (error) {
      console.error('Failed to switch user:', error);
      logout();
      navigate('/admin/login');
    }
  };

  /** Per weekday from Operating hours when set; else global Booking “Table reservation length”. */
  const getTableReservationDurationMinutes = (forDateStr) => {
    const dateStr = forDateStr || format(selectedDate, 'yyyy-MM-dd');
    let dayKey;
    try {
      dayKey = format(parse(dateStr, 'yyyy-MM-dd', new Date()), 'EEEE').toLowerCase();
    } catch {
      dayKey = null;
    }
    const globalDefault = Math.max(
      15,
      parseInt(String(restaurant?.settings?.table_booking_duration_minutes ?? ''), 10) || 120
    );
    if (!dayKey) return globalDefault;
    const daySchedule = restaurant?.settings?.opening_hours_v2?.[dayKey];
    const perDay = daySchedule?.table_booking_duration_minutes;
    if (perDay != null && perDay !== '') {
      const n = parseInt(String(perDay), 10);
      if (Number.isFinite(n)) return Math.max(15, n);
    }
    return globalDefault;
  };

  const bookingTimeToMinutes = (hhmm) => {
    if (!hhmm || typeof hhmm !== 'string') return 0;
    const [h, m] = hhmm.split(':').map((x) => parseInt(x, 10));
    return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
  };

  const bookingWindowsOverlap = (timeA, timeB, durationMinutes) => {
    const a0 = bookingTimeToMinutes(timeA);
    const b0 = bookingTimeToMinutes(timeB);
    return a0 < b0 + durationMinutes && b0 < a0 + durationMinutes;
  };

  /** True if another active (incomplete) booking already holds this table for an overlapping period. */
  const tableOverlapsActiveBooking = (
    tableId,
    bookingDate,
    bookingTime,
    excludeBookingId,
    list,
    durationMinutesOverride = null
  ) => {
    const dur = durationMinutesOverride ?? getTableReservationDurationMinutes(bookingDate);
    return list.some((b) => {
      if (excludeBookingId != null && b.id === excludeBookingId) return false;
      if (b.booking_date !== bookingDate) return false;
      if (['completed', 'cancelled', 'no-show'].includes(b.status)) return false;
      if (!b.assigned_tables?.includes(tableId)) return false;
      return bookingWindowsOverlap(bookingTime, b.booking_time, dur);
    });
  };

  const recomputeEditDialogTableAvailability = useCallback(
    (booking) => {
      if (!booking?.booking_time) return;
      const dateStr = booking.booking_date || format(selectedDate, 'yyyy-MM-dd');
      const dur = getTableReservationDurationMinutes(dateStr);
      const availabilityMap = {};
      tables.filter((t) => t.is_active !== false).forEach((table) => {
        availabilityMap[table.id] = !tableOverlapsActiveBooking(
          table.id,
          dateStr,
          booking.booking_time,
          booking.id,
          bookings,
          dur
        );
      });
      setTableAvailability(availabilityMap);
    },
    [bookings, selectedDate, tables, restaurant?.settings?.table_booking_duration_minutes, restaurant?.settings?.opening_hours_v2]
  );

  useEffect(() => {
    if (!editDialogOpen || !editingBooking) return;
    recomputeEditDialogTableAvailability(editingBooking);
  }, [
    editDialogOpen,
    editingBooking?.id,
    editingBooking?.booking_time,
    recomputeEditDialogTableAvailability,
  ]);

  // Get blocked slot numbers for a specific time
  const getBlockedSlotsForTime = (time) => {
    const blocked = blockedTimes.find(b => b.time === time);
    return blocked?.blocked_slot_numbers || [];
  };

  // Block/unblock specific slots for a time
  const handleBlockSlots = async () => {
    if (!selectedTimeSlot || slotsToBlock.length === 0) return;
    
    setBlockingSlots(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      await axios.post(`${API}/tablet/blocked-slots`, {
        date: dateStr,
        time: selectedTimeSlot,
        blocked_slot_numbers: slotsToBlock,
        reason: 'Manually blocked'
      });
      
      toast.success(`Blocked ${slotsToBlock.length} slot(s) at ${selectedTimeSlot}`);
      setTimeSlotDialogOpen(false);
      setSlotsToBlock([]);
      fetchDayBookings(); // Refresh to get updated blocked slots
    } catch (error) {
      toast.error('Failed to block slots');
    } finally {
      setBlockingSlots(false);
    }
  };

  // Unblock all slots for a time
  const handleUnblockTime = async () => {
    if (!selectedTimeSlot) return;
    
    setBlockingSlots(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      await axios.delete(`${API}/tablet/blocked-slots/${dateStr}/${selectedTimeSlot}`);
      
      toast.success(`Unblocked ${selectedTimeSlot}`);
      setTimeSlotDialogOpen(false);
      fetchDayBookings();
    } catch (error) {
      toast.error('Failed to unblock time');
    } finally {
      setBlockingSlots(false);
    }
  };

  // Toggle a slot in the selection
  const toggleSlotToBlock = (slotNum) => {
    setSlotsToBlock(prev => 
      prev.includes(slotNum) 
        ? prev.filter(s => s !== slotNum)
        : [...prev, slotNum]
    );
  };

  // Select all slots
  const selectAllSlots = () => {
    const settings = restaurant?.settings || {};
    const tablesPerSlot = settings.tables_per_slot || 4;
    setSlotsToBlock(Array.from({ length: tablesPerSlot }, (_, i) => i + 1));
  };

  const updateBooking = async (bookingId, updates) => {
    try {
      const response = await axios.put(`${API}/tablet/bookings/${bookingId}`, updates);
      setBookings((prev) => {
        const exists = prev.some((b) => b.id === bookingId);
        if (!exists) return [...prev, response.data];
        return prev.map((b) => (b.id === bookingId ? response.data : b));
      });
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to update booking';
      toast.error(errorMessage);
      return false;
    }
  };

  const upsertBookingLocally = (booking) => {
    if (!booking?.id) return;
    setBookings((prev) => {
      const idx = prev.findIndex((b) => b.id === booking.id);
      if (idx === -1) return [...prev, booking];
      const next = [...prev];
      next[idx] = booking;
      return next;
    });
  };

  const updateStatus = async (bookingId, newStatus) => {
    const target = bookings.find((b) => b.id === bookingId);
    if (target && !confirmUnpaidDepositArrival(target, newStatus)) return;
    if (target && !confirmDepositRefundOnCancel(target, newStatus)) return;
    const updated = await updateBooking(bookingId, { status: newStatus });
    if (updated) {
      const depositNote = newStatus === 'cancelled' ? describeCancelDepositOutcome(target, updated) : null;
      toast.success(depositNote ? `Status updated to ${newStatus}. ${depositNote}` : `Status updated to ${newStatus}`);
    }
  };

  const handleEditSave = async () => {
    if (!editingBooking) return;
    const original = bookings.find((b) => b.id === editingBooking.id);
    const statusChanging = original?.status !== editingBooking.status;
    if (statusChanging && !confirmUnpaidDepositArrival(editingBooking, editingBooking.status)) {
      return;
    }
    if (statusChanging && !confirmDepositRefundOnCancel(editingBooking, editingBooking.status)) {
      return;
    }
    setSavingEdit(true);
    const payload = {
      status: editingBooking.status,
      party_size: parseInt(String(editingBooking.party_size), 10) || 2,
      // Always send as string (even "") so backend always applies the update.
      // Backend converts "" → null in DB. Sending null would skip the update entirely.
      allergies: (editingBooking.allergies ?? '').trim(),
      special_requests: (editingBooking.special_requests ?? '').trim(),
      assigned_tables: [...new Set(editingBooking.assigned_tables || [])]
    };
    // Only send booking_time when staff explicitly unlocked time editing.
    if (editTimeUnlocked && editingBooking.booking_time) {
      payload.booking_time = editingBooking.booking_time;
    }
    const updated = await updateBooking(editingBooking.id, payload);
    if (updated) {
      const depositNote =
        statusChanging && editingBooking.status === 'cancelled'
          ? describeCancelDepositOutcome(original, updated)
          : null;
      toast.success(depositNote ? `Booking updated. ${depositNote}` : 'Booking updated');
      setEditDialogOpen(false);
      setEditingBooking(null);
      setEditTimeUnlocked(false);
      fetchDayBookings();
      fetchCalendarData();
    }
    setSavingEdit(false);
  };

  const openEditDialog = (booking) => {
    setEditingBooking({
      ...booking,
      assigned_tables: booking.assigned_tables || []
    });
    setEditTimeUnlocked(false);
    setPhoneDepositAmount('');
    setEditDialogOpen(true);
    recomputeEditDialogTableAvailability(booking);
  };

  const handleMarkDepositPaid = async () => {
    if (!editingBooking) return;
    const amount = parseFloat(String(phoneDepositAmount).replace(/[^0-9.]/g, ''));
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error('Please enter a valid amount greater than 0');
      return;
    }
    setMarkingDepositPaid(true);
    try {
      await axios.post(`${API}/admin/bookings/${editingBooking.id}/mark-deposit-paid`, {
        amount_paid: amount,
      });
      toast.success(`Deposit of £${amount.toFixed(2)} marked as paid`);
      setPhoneDepositAmount('');
      // Refresh local state and re-open edit dialog with updated booking
      await fetchDayBookings();
      setEditDialogOpen(false);
      setEditingBooking(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark deposit as paid');
    } finally {
      setMarkingDepositPaid(false);
    }
  };

  /**
   * Assign tables in Edit Booking.
   * - Click an unassigned table → **move** booking to that table only (one table is the usual case).
   * - Shift+click → **add** that table (combine tables for a larger group).
   * - Click an already-assigned table → remove it from the assignment.
   */
  const toggleTableAssignment = (tableId, { addTable = false } = {}) => {
    if (!editingBooking) return;

    const current = [...new Set(editingBooking.assigned_tables || [])];
    const isCurrentlyAssigned = current.includes(tableId);

    const isAvailable = tableAvailability[tableId] === true;

    if (!isCurrentlyAssigned && !isAvailable) {
      toast.error('This table is still in use for an overlapping reservation (or completes first)');
      return;
    }

    if (isCurrentlyAssigned) {
      setEditingBooking({
        ...editingBooking,
        assigned_tables: current.filter((id) => id !== tableId)
      });
      return;
    }

    if (addTable && current.length > 0) {
      setEditingBooking({
        ...editingBooking,
        assigned_tables: [...current, tableId]
      });
    } else {
      setEditingBooking({
        ...editingBooking,
        assigned_tables: [tableId]
      });
    }
  };

  // Open quick booking dialog from timeline
  const handleAddBookingFromTimeline = () => {
    setTimeSlotDialogOpen(false);
    setQuickBookingData({
      customer_name: '',
      customer_email: '',
      customer_phone: '',
      party_size: '2',
      allergies: '',
      booking_time: selectedTimeSlot,
      assigned_tables: [],
      is_staff_hold: false
    });
    setQuickBookingDialogOpen(true);
  };

  const resetQuickBookingForm = () => ({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    party_size: '2',
    allergies: '',
    booking_time: '',
    assigned_tables: [],
    is_staff_hold: false
  });

  const handleQuickBookingSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const isStaffHold = quickBookingData.is_staff_hold;
    const endpoint = isStaffHold ? `${API}/tablet/bookings/staff-only` : `${API}/tablet/bookings/walk-in`;
    const defaultName = isStaffHold ? 'Staff hold' : 'Walk-in';
    try {
      const response = await axios.post(endpoint, {
        customer_name: quickBookingData.customer_name || defaultName,
        customer_phone: quickBookingData.customer_phone || '',
        party_size: parseInt(quickBookingData.party_size, 10),
        restaurant_id: restaurant.id,
        booking_date: format(selectedDate, 'yyyy-MM-dd'),
        booking_time: quickBookingData.booking_time,
        customer_email: quickBookingData.customer_email || `walkin-${Date.now()}@temp.com`,
        allergies: quickBookingData.allergies || null,
        assigned_tables: quickBookingData.assigned_tables || []
      });
      upsertBookingLocally(response.data.booking);
      setQuickBookingDialogOpen(false);
      setQuickBookingData(resetQuickBookingForm());
      toast.success(isStaffHold ? 'Staff hold added — not on public booking' : 'Walk-in added');
      fetchCalendarData();
    } catch (error) {
      toast.error(error.response?.data?.detail || (isStaffHold ? 'Failed to add staff hold' : 'Failed to add walk-in'));
    } finally {
      setSubmitting(false);
    }
  };

  // Toggle table for quick booking
  const toggleQuickBookingTable = (tableId) => {
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    const t = quickBookingData.booking_time;
    const current = quickBookingData.assigned_tables || [];
    if (!current.includes(tableId)) {
      if (tableOverlapsActiveBooking(tableId, dateStr, t, null, bookings, null)) {
        toast.error('That table is already in use for this period');
        return;
      }
    }
    const newTables = current.includes(tableId)
      ? current.filter((id) => id !== tableId)
      : [...current, tableId];
    setQuickBookingData({ ...quickBookingData, assigned_tables: newTables });
  };

  // Phone Booking Functions
  const resetPhoneBooking = () => {
    setPhoneBookingStep(1);
    setPhoneData({
      customer_phone: '',
      customer_name: '',
      customer_email: '',
      party_size: '2',
      allergies: '',
      special_requests: '',
      booking_date: null,
      booking_time: ''
    });
    setCustomerFound(false);
    setAvailableSlots([]);
    setPhoneWaiveDeposit(!!user?.phone_bookings_skip_deposit);
  };

  const handlePhoneLookup = async () => {
    if (!phoneData.customer_phone || phoneData.customer_phone.length < 6) {
      toast.error('Please enter a valid phone number');
      return;
    }
    
    setSearchingCustomer(true);
    try {
      const response = await axios.get(`${API}/tablet/customer-lookup`, {
        params: { phone: phoneData.customer_phone },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.data.found) {
        const customer = response.data.customer;
        setPhoneData(prev => ({
          ...prev,
          customer_name: customer.name || '',
          customer_email: customer.email || '',
          allergies: customer.allergies || ''
        }));
        setCustomerFound(true);
        toast.success('Customer found! Details auto-filled.');
      } else {
        setCustomerFound(false);
        toast.info('New customer - please fill in details');
      }
      setPhoneBookingStep(2);
    } catch (error) {
      toast.error('Error looking up customer');
      setPhoneBookingStep(2);
    } finally {
      setSearchingCustomer(false);
    }
  };

  const appendPhoneLookupDigit = (digit) => {
    setPhoneData((prev) => ({
      ...prev,
      customer_phone: `${prev.customer_phone || ''}${digit}`.slice(0, 20),
    }));
  };

  const backspacePhoneLookupDigit = () => {
    setPhoneData((prev) => ({
      ...prev,
      customer_phone: (prev.customer_phone || '').slice(0, -1),
    }));
  };

  const clearPhoneLookupDigits = () => {
    setPhoneData((prev) => ({ ...prev, customer_phone: '' }));
  };

  const handleDateSelect = async (date) => {
    setPhoneData(prev => ({ ...prev, booking_date: date, booking_time: '' }));
    setLoadingSlots(true);
    
    try {
      const response = await axios.get(`${API}/availability/${restaurant.id}`, {
        params: { 
          date: format(date, 'yyyy-MM-dd'),
          party_size: parseInt(phoneData.party_size)
        }
      });
      
      if (response.data.is_closed) {
        setAvailableSlots([]);
        toast.error('Restaurant is closed on this date');
      } else {
        // Filter to only available slots
        const available = response.data.slots.filter(slot => slot.available_tables > 0);
        setAvailableSlots(available);
        if (available.length === 0) {
          toast.error('No availability for this date');
        }
      }
    } catch (error) {
      toast.error('Failed to load availability');
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  // Check if deposit is required for phone booking
  const checkPhoneDepositRequired = async () => {
    if (!phoneData.booking_date) return false;
    
    try {
      const response = await axios.post(`${API}/deposit/check`, {
        restaurant_id: restaurant.id,
        booking_date: format(phoneData.booking_date, 'yyyy-MM-dd'),
        party_size: parseInt(phoneData.party_size)
      });
      setPhoneDepositInfo(response.data);
      return response.data.deposit_required;
    } catch (error) {
      console.error('Failed to check deposit:', error);
      setPhoneDepositInfo(null);
      return false;
    }
  };

  const handlePhoneBookingSubmit = async () => {
    if (!phoneData.customer_name || !phoneData.booking_date || !phoneData.booking_time) {
      toast.error('Please fill in all required fields');
      return;
    }
    const emailOk = phoneData.customer_email && phoneData.customer_email.includes('@');
    if (!phoneWaiveDeposit && !emailOk) {
      toast.error('Email is required unless you choose no deposit required for this booking.');
      return;
    }

    if (phoneSubmitLockRef.current || submitting || sendingDepositEmail) {
      return;
    }
    phoneSubmitLockRef.current = true;

    try {
      if (phoneWaiveDeposit) {
        await createPhoneBooking();
        return;
      }

      // Check if deposit is required
      const depositRequired = await checkPhoneDepositRequired();
      
      if (depositRequired) {
        // Send deposit link immediately (email is required in step 2).
        await sendDepositEmail();
        return;
      }
      
      // No deposit required - create booking directly
      await createPhoneBooking();
    } finally {
      phoneSubmitLockRef.current = false;
    }
  };

  const createPhoneBooking = async () => {
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/tablet/bookings/phone`, {
        customer_name: phoneData.customer_name,
        customer_email: phoneData.customer_email?.trim() || null,
        customer_phone: phoneData.customer_phone,
        party_size: parseInt(phoneData.party_size),
        restaurant_id: restaurant.id,
        booking_date: format(phoneData.booking_date, 'yyyy-MM-dd'),
        booking_time: phoneData.booking_time,
        allergies: phoneData.allergies || null,
        special_requests: phoneData.special_requests || null,
        waive_deposit: phoneWaiveDeposit,
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // If booking is for selected date, add to current view
      if (format(phoneData.booking_date, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd')) {
        upsertBookingLocally(response.data.booking);
      }
      
      setPhoneDialogOpen(false);
      resetPhoneBooking();
      toast.success('Phone booking created!');
      fetchCalendarData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setSubmitting(false);
    }
  };

  const sendDepositEmail = async () => {
    if (!phoneData.customer_email || !phoneData.customer_email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSendingDepositEmail(true);
    try {
      const response = await axios.post(`${API}/tablet/bookings/send-deposit-link`, {
        customer_name: phoneData.customer_name,
        customer_email: phoneData.customer_email,
        customer_phone: phoneData.customer_phone,
        party_size: parseInt(phoneData.party_size),
        restaurant_id: restaurant.id,
        booking_date: format(phoneData.booking_date, 'yyyy-MM-dd'),
        booking_time: phoneData.booking_time,
        allergies: phoneData.allergies || null,
        special_requests: phoneData.special_requests || null
      });
      
      if (response.data.checkout_url) {
        setDepositLinkDialog({
          open: true,
          url: response.data.checkout_url,
          customerPhone: phoneData.customer_phone,
          customerEmail: phoneData.customer_email
        });
        toast.success('Deposit payment link sent!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send deposit link');
    } finally {
      setSendingDepositEmail(false);
    }
  };

  // Get unique zones from tables
  const getUniqueZones = () => {
    const zones = [...new Set(tables.map(t => t.zone || 'Unassigned').filter(Boolean))];
    return zones.sort();
  };

  // Filter tables by selected zone
  const getFilteredTables = () => {
    if (selectedZone === 'all') return tables;
    return tables.filter(t => (t.zone || 'Unassigned') === selectedZone);
  };

  const getStatusBadge = (status) => {
    const option = STATUS_OPTIONS.find(s => s.value === status);
    return option?.color || 'bg-slate-100 text-slate-800';
  };

  const getTableName = (tableId) => {
    const table = tables.find(t => t.id === tableId);
    return table?.name || 'Unknown';
  };

  // Group bookings by hour
  const bookingsByHour = bookings.reduce((acc, booking) => {
    const hour = booking.booking_time.split(':')[0];
    if (!acc[hour]) acc[hour] = [];
    acc[hour].push(booking);
    return acc;
  }, {});

  // Generate time slots for the day based on restaurant settings
  const getTimeSlots = () => {
    const settings = restaurant?.settings || {};
    const interval = settings.booking_interval_minutes || 30;
    
    // Keep a broad operational timeline on tablet:
    // - start at 05:00 for early VIP/breakfast handling
    // - run through end-of-day so late event bookings can be added
    const openHour = 5;
    const closeHour = 24;
    
    const slots = [];
    for (let hour = openHour; hour < closeHour; hour++) {
      for (let min = 0; min < 60; min += interval) {
        slots.push(`${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`);
      }
    }
    return slots;
  };

  // Check if a time slot is during operating hours
  const isOperatingHours = (timeStr) => {
    const dayName = format(selectedDate, 'EEEE').toLowerCase();
    const openingHours = restaurant?.settings?.opening_hours_v2?.[dayName];
    
    if (!openingHours || openingHours.closed) {
      return false;
    }
    
    if (!openingHours.periods || openingHours.periods.length === 0) {
      return false;
    }
    
    // Check if time falls within any operating period
    const [hours, minutes] = timeStr.split(':').map(Number);
    const timeMinutes = hours * 60 + minutes;
    
    return openingHours.periods.some(period => {
      const [openH, openM] = period.open.split(':').map(Number);
      const [closeH, closeM] = period.close.split(':').map(Number);
      const openMinutes = openH * 60 + openM;
      const closeMinutes = closeH * 60 + closeM;
      
      return timeMinutes >= openMinutes && timeMinutes < closeMinutes;
    });
  };

  const timeSlots = getTimeSlots();
  
  // Get all hours displayed by the generated timeline.
  const hours = Array.from(new Set(timeSlots.map((slot) => slot.split(':')[0])));

  // Group bookings by table for table view
  const bookingsByTable = tables.reduce((acc, table) => {
    acc[table.id] = bookings.filter(b => 
      b.assigned_tables?.includes(table.id) && 
      !['completed', 'cancelled', 'no-show'].includes(b.status)
    );
    return acc;
  }, {});

  /** When multiple bookings share a table, prefer seated, then arrived, then earliest time. */
  const pickPrimaryBookingForTable = (tableBookings) => {
    if (!tableBookings?.length) return null;
    const seated = tableBookings.find((b) => b.status === 'seated');
    if (seated) return seated;
    const arrived = tableBookings.find((b) => b.status === 'arrived');
    if (arrived) return arrived;
    return [...tableBookings].sort((a, b) => a.booking_time.localeCompare(b.booking_time))[0];
  };

  // Unassigned bookings
  const unassignedBookings = bookings.filter(b => 
    (!b.assigned_tables || b.assigned_tables.length === 0) &&
    !['completed', 'cancelled', 'no-show'].includes(b.status)
  );

  const openTableDetailDialog = (table) => {
    if (viewMode !== 'tables') return;
    setSelectedTableForAssign(table);
    setAssignDialogOpen(true);
  };

  const handleAssignBookingToSelectedTable = async (booking) => {
    if (!selectedTableForAssign) return;
    const success = await updateBooking(booking.id, {
      assigned_tables: [selectedTableForAssign.id]
    });
    if (success) {
      toast.success(`${booking.customer_name} → Table ${selectedTableForAssign.name}`);
      setAssignDialogOpen(false);
      setSelectedTableForAssign(null);
      fetchDayBookings();
      fetchCalendarData();
    }
  };

  const isBookingActive = (b) => !['completed', 'cancelled', 'no-show'].includes(b.status);

  const getBookingsForTableSorted = (tableId) =>
    [...bookings]
      .filter(
        (b) =>
          b.assigned_tables?.includes(tableId) &&
          !['cancelled', 'no-show'].includes(b.status)
      )
      .sort((a, b) => a.booking_time.localeCompare(b.booking_time));

  const suggestTablesForBooking = (booking, list = bookings) => {
    const p = parseInt(booking.party_size, 10) || 1;
    const dateStr = booking.booking_date || format(selectedDate, 'yyyy-MM-dd');
    return tables
      .filter((t) => t.is_active !== false && t.capacity >= p)
      .filter(
        (t) =>
          !tableOverlapsActiveBooking(t.id, dateStr, booking.booking_time, booking.id, list, null)
      )
      .sort(
        (a, b) =>
          a.capacity - p - (b.capacity - p) || String(a.name).localeCompare(String(b.name))
      );
  };

  const handleWalkin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/tablet/bookings/walk-in`, {
        ...walkinData,
        party_size: parseInt(walkinData.party_size),
        restaurant_id: restaurant.id,
        booking_date: format(selectedDate, 'yyyy-MM-dd'),
        booking_time: format(new Date(), 'HH:mm'),
        customer_email: walkinData.customer_email || `walkin-${Date.now()}@temp.com`,
        allergies: walkinData.allergies || null
      });
      const newBooking = response.data.booking;
      const mergedBookings = [...bookings, newBooking];

      const party = parseInt(walkinData.party_size, 10);
      let assignIds = [];

      if (walkinPlacement.preferTableId) {
        const tbl = tables.find((t) => t.id === walkinPlacement.preferTableId);
        if (
          tbl &&
          party <= tbl.capacity &&
          tbl.is_active !== false &&
          !tableOverlapsActiveBooking(
            tbl.id,
            format(selectedDate, 'yyyy-MM-dd'),
            newBooking.booking_time,
            newBooking.id,
            mergedBookings,
            null
          )
        ) {
          assignIds = [tbl.id];
        } else if (walkinPlacement.smartPlace) {
          const ranked = suggestTablesForBooking(newBooking, mergedBookings);
          if (ranked[0]) assignIds = [ranked[0].id];
        }
      } else if (walkinPlacement.smartPlace && viewMode === 'tables') {
        const ranked = suggestTablesForBooking(newBooking, mergedBookings);
        if (ranked[0]) assignIds = [ranked[0].id];
      }

      if (assignIds.length) {
        await updateBooking(newBooking.id, { assigned_tables: assignIds });
        toast.success('Walk-in added and placed on a table');
      } else {
        setBookings((prev) => (prev.some((b) => b.id === newBooking.id) ? prev : [...prev, newBooking]));
        toast.success('Walk-in added');
      }

      setWalkinDialogOpen(false);
      setWalkinData({ customer_name: '', customer_email: '', customer_phone: '', party_size: '2', allergies: '' });
      setWalkinPlacement({ smartPlace: true, preferTableId: null });
      fetchCalendarData();
    } catch (error) {
      toast.error('Failed to add walk-in');
    } finally {
      setSubmitting(false);
    }
  };

  /** Best-fit table for this booking (party size + slot), then assign */
  const handleSmartAssignTableForBooking = async (booking) => {
    const ranked = suggestTablesForBooking(booking);
    const best = ranked[0];
    if (!best) {
      toast.info('No free table fits this party at this time slot.');
      return;
    }
    const success = await updateBooking(booking.id, {
      assigned_tables: [best.id]
    });
    if (success) {
      toast.success(`${booking.customer_name} → Table ${best.name}`);
      fetchDayBookings();
      fetchCalendarData();
    }
  };

  // Derive today's named periods from opening_hours_v2
  const todayPeriods = (() => {
    const dayKey = format(selectedDate, 'EEEE').toLowerCase();
    const daySchedule = restaurant?.settings?.opening_hours_v2?.[dayKey];
    if (!daySchedule || daySchedule.closed) return [];
    return (daySchedule.periods || []).filter(p => p.title && p.open && p.close);
  })();

  // Filter bookings by the selected named period (or all)
  const bookingsInPeriod = (bks) => {
    if (selectedPeriod === 'all' || todayPeriods.length < 2) return bks;
    const period = todayPeriods.find(p => p.title === selectedPeriod);
    if (!period) return bks;
    return bks.filter(b => {
      const t = b.booking_time || '';
      if (!t) return false;
      return t >= period.open && t < period.close;
    });
  };

  const sumGuestsForStatuses = (statuses) =>
    bookingsInPeriod(bookings)
      .filter((b) => statuses.includes(b.status))
      .reduce((sum, b) => sum + (parseInt(String(b.party_size || 0), 10) || 0), 0);

  const countBookingsForStatuses = (statuses) =>
    bookingsInPeriod(bookings).filter((b) => statuses.includes(b.status)).length;

  const expectedBookings = countBookingsForStatuses(['confirmed']);
  const seatedBookings = countBookingsForStatuses(['seated', 'completed']);
  const expectedGuests = sumGuestsForStatuses(['confirmed']);
  const seatedGuests = sumGuestsForStatuses(['seated', 'completed']);

  return (
    <div
      className="min-h-screen min-h-[100dvh] bg-slate-50 pb-[calc(5.25rem+env(safe-area-inset-bottom))] md:pb-[env(safe-area-inset-bottom)]"
      data-testid="tablet-view"
    >
      {/* Header — stacked on narrow screens for thumb reach */}
      <div className="bg-white border-b border-border/50 sticky top-0 z-40 pt-[max(0.5rem,env(safe-area-inset-top))]">
        <div className="flex flex-col gap-3 px-3 py-3 sm:px-6 sm:py-4">
          <div className="flex items-start justify-between gap-2">
            <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-4">
              <Link to="/admin/dashboard">
                <Button variant="ghost" size="icon" className="shrink-0 touch-manipulation" data-testid="back-to-admin">
                  <ChevronLeft className="h-5 w-5" />
                </Button>
              </Link>
              <div className="min-w-0">
                <h1 className="font-heading truncate text-lg font-bold sm:text-xl">{restaurant?.name || 'Restaurant'}</h1>
                <p className="truncate text-xs text-muted-foreground sm:text-sm">
                  {format(selectedDate, 'EEEE, MMMM d, yyyy')}
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
            <div className="flex w-full max-w-md rounded-lg bg-secondary p-1 sm:w-auto">
              <Button
                variant={viewMode === 'timeline' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('timeline')}
                className="h-10 flex-1 gap-1 touch-manipulation sm:h-9 sm:flex-initial"
                data-testid="view-timeline"
              >
                <Clock className="h-4 w-4" />
                <span className="sm:hidden">Time</span>
                <span className="hidden sm:inline">Timeline</span>
              </Button>
              <Button
                variant={viewMode === 'tables' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('tables')}
                className="h-10 flex-1 gap-1 touch-manipulation sm:h-9 sm:flex-initial"
                data-testid="view-tables"
              >
                <LayoutGrid className="h-4 w-4" />
                Tables
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-1 sm:justify-end sm:gap-2">
              <Dialog
                open={walkinDialogOpen}
                onOpenChange={(open) => {
                  setWalkinDialogOpen(open);
                  if (!open) {
                    setWalkinPlacement({ smartPlace: viewMode === 'tables', preferTableId: null });
                  }
                }}
              >
                <DialogTrigger asChild>
                  <Button
                    className="h-11 w-full gap-2 touch-manipulation sm:h-10 sm:w-auto"
                    data-testid="add-walkin-btn"
                    onClick={() =>
                      setWalkinPlacement({
                        smartPlace: viewMode === 'tables',
                        preferTableId: null
                      })
                    }
                  >
                    <Plus className="h-4 w-4" />
                    Walk-in
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-h-[min(85dvh,560px)] w-[calc(100vw-1.25rem)] max-w-lg overflow-y-auto overscroll-contain p-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:max-h-[90vh] sm:p-6">
                <DialogHeader>
                  <DialogTitle className="font-heading">Add Walk-in Guest</DialogTitle>
                  <DialogDescription>Quick entry for walk-in customers</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleWalkin} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Name</Label>
                    <Input
                      value={walkinData.customer_name}
                      onChange={(e) => setWalkinData({ ...walkinData, customer_name: e.target.value })}
                      placeholder="Guest name"
                      required
                      data-testid="walkin-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone (optional)</Label>
                    <Input
                      value={walkinData.customer_phone}
                      onChange={(e) => setWalkinData({ ...walkinData, customer_phone: e.target.value })}
                      placeholder="+1 555-0000"
                      data-testid="walkin-phone"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Party Size</Label>
                    <Select 
                      value={walkinData.party_size} 
                      onValueChange={(v) => setWalkinData({ ...walkinData, party_size: v })}
                    >
                      <SelectTrigger data-testid="walkin-party-size">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1,2,3,4,5,6,7,8,9,10,11,12].map(n => (
                          <SelectItem key={n} value={String(n)}>{n} guests</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                      Allergies / Dietary
                    </Label>
                    <Input
                      value={walkinData.allergies}
                      onChange={(e) => setWalkinData({ ...walkinData, allergies: e.target.value })}
                      placeholder="e.g., Nut allergy, Gluten-free"
                      data-testid="walkin-allergies"
                    />
                  </div>
                  {(viewMode === 'tables' || walkinPlacement.preferTableId) && (
                    <div className="space-y-3 rounded-lg border border-violet-200 bg-violet-50/90 p-3">
                      <div className="flex items-center gap-2 text-sm font-semibold text-violet-900">
                        <Sparkles className="h-4 w-4 shrink-0" />
                        Table placement
                      </div>
                      {walkinPlacement.preferTableId && (
                        <p className="text-xs text-violet-900">
                          Prefer: Table{' '}
                          <strong>{tables.find((t) => t.id === walkinPlacement.preferTableId)?.name}</strong>
                          . If that slot is busy, smart pick runs when enabled below.
                        </p>
                      )}
                      <div className="flex items-start gap-3">
                        <Checkbox
                          id="walkin-smart-place"
                          checked={walkinPlacement.smartPlace}
                          onCheckedChange={(v) =>
                            setWalkinPlacement((p) => ({ ...p, smartPlace: v === true }))
                          }
                          className="mt-0.5"
                        />
                        <label htmlFor="walkin-smart-place" className="cursor-pointer text-sm leading-snug text-violet-950">
                          Smart place on the best free table for this party size and time slot (recommended on mobile)
                        </label>
                      </div>
                    </div>
                  )}
                  <div className="flex gap-3 pt-2">
                    <Button type="button" variant="outline" onClick={() => setWalkinDialogOpen(false)} className="h-11 flex-1 touch-manipulation sm:h-10">
                      Cancel
                    </Button>
                    <Button type="submit" disabled={submitting} className="h-11 flex-1 touch-manipulation sm:h-10" data-testid="walkin-submit">
                      {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add Guest'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
            
            {/* Phone Booking Dialog */}
            <Dialog open={phoneDialogOpen} onOpenChange={(open) => {
              setPhoneDialogOpen(open);
              if (!open) resetPhoneBooking();
            }}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  className="h-11 w-full gap-2 touch-manipulation sm:h-10 sm:w-auto"
                  data-testid="add-phone-booking-btn"
                >
                  <Phone className="h-4 w-4" />
                  <span className="hidden sm:inline">Phone Booking</span>
                  <span className="sm:hidden">Phone</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[min(85dvh,560px)] w-[calc(100vw-1.25rem)] max-w-md overflow-y-auto overscroll-contain pb-[max(1rem,env(safe-area-inset-bottom))] sm:max-h-[90vh]">
                <DialogHeader>
                  <DialogTitle className="font-heading">Phone Booking</DialogTitle>
                  <DialogDescription>
                    {phoneBookingStep === 1 && "Enter customer phone to find their details"}
                    {phoneBookingStep === 2 && "Confirm customer details and party size"}
                    {phoneBookingStep === 3 && "Select date and available time"}
                  </DialogDescription>
                </DialogHeader>
                
                {/* Step 1: Phone Lookup */}
                {phoneBookingStep === 1 && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Phone Number</Label>
                      <Input
                        value={phoneData.customer_phone}
                        onChange={(e) => setPhoneData({ ...phoneData, customer_phone: e.target.value })}
                        placeholder="+1 555-0000"
                        autoFocus
                        data-testid="phone-lookup-input"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((digit) => (
                        <Button
                          key={digit}
                          type="button"
                          variant="outline"
                          className="h-12 text-lg font-semibold touch-manipulation"
                          onClick={() => appendPhoneLookupDigit(digit)}
                        >
                          {digit}
                        </Button>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        className="h-12 text-sm touch-manipulation"
                        onClick={clearPhoneLookupDigits}
                      >
                        Clear
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        className="h-12 text-lg font-semibold touch-manipulation"
                        onClick={() => appendPhoneLookupDigit('0')}
                      >
                        0
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        className="h-12 touch-manipulation"
                        onClick={backspacePhoneLookupDigit}
                      >
                        <Delete className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex gap-3">
                      <Button variant="outline" onClick={() => setPhoneDialogOpen(false)} className="flex-1">
                        Cancel
                      </Button>
                      <Button 
                        onClick={handlePhoneLookup} 
                        disabled={searchingCustomer}
                        className="flex-1"
                        data-testid="phone-lookup-btn"
                      >
                        {searchingCustomer ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Find Customer'}
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Step 2: Customer Details */}
                {phoneBookingStep === 2 && (
                  <div className="space-y-4">
                    {customerFound && (
                      <div className="p-2 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-700">
                        ✓ Returning customer - details auto-filled
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label>Name *</Label>
                      <Input
                        value={phoneData.customer_name}
                        onChange={(e) => setPhoneData({ ...phoneData, customer_name: e.target.value })}
                        placeholder="Customer name"
                        required
                        data-testid="phone-customer-name"
                      />
                    </div>
                    <div className="flex items-start gap-3 rounded-lg border border-border/60 bg-muted/30 p-3">
                      <Checkbox
                        id="tablet-phone-waive-deposit"
                        checked={phoneWaiveDeposit}
                        onCheckedChange={(v) => setPhoneWaiveDeposit(v === true)}
                      />
                      <div className="space-y-1">
                        <Label htmlFor="tablet-phone-waive-deposit" className="cursor-pointer font-medium leading-none">
                          No deposit required
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Confirm without a deposit or guest email. No deposit request email is sent.
                        </p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Email {phoneWaiveDeposit ? '(optional)' : '*'}</Label>
                      <Input
                        value={phoneData.customer_email}
                        onChange={(e) => setPhoneData({ ...phoneData, customer_email: e.target.value })}
                        placeholder="email@example.com"
                        type="email"
                        data-testid="phone-customer-email"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Party Size</Label>
                      <Select 
                        value={phoneData.party_size} 
                        onValueChange={(v) => setPhoneData({ ...phoneData, party_size: v })}
                      >
                        <SelectTrigger data-testid="phone-party-size">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[1,2,3,4,5,6,7,8,9,10,11,12].map(n => (
                            <SelectItem key={n} value={String(n)}>{n} guests</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-amber-500" />
                        Allergies / Dietary
                      </Label>
                      <Input
                        value={phoneData.allergies}
                        onChange={(e) => setPhoneData({ ...phoneData, allergies: e.target.value })}
                        placeholder="e.g., Nut allergy, Gluten-free"
                        data-testid="phone-allergies"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Special Requests</Label>
                      <Textarea
                        value={phoneData.special_requests}
                        onChange={(e) => setPhoneData({ ...phoneData, special_requests: e.target.value })}
                        placeholder="e.g., Birthday table, quiet area, highchair"
                        rows={3}
                        data-testid="phone-special-requests"
                      />
                    </div>
                    <div className="flex gap-3">
                      <Button variant="outline" onClick={() => setPhoneBookingStep(1)} className="flex-1">
                        Back
                      </Button>
                      <Button 
                        onClick={() => setPhoneBookingStep(3)} 
                        disabled={
                          !phoneData.customer_name ||
                          (!phoneWaiveDeposit && (!phoneData.customer_email || !phoneData.customer_email.includes('@')))
                        }
                        className="flex-1"
                        data-testid="phone-next-step"
                      >
                        Select Time
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Step 3: Date & Time Selection */}
                {phoneBookingStep === 3 && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Select Date</Label>
                      <Calendar
                        mode="single"
                        selected={phoneData.booking_date}
                        onSelect={handleDateSelect}
                        disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                        className="rounded-md border"
                      />
                    </div>
                    
                    {phoneData.booking_date && (
                      <div className="space-y-2">
                        <Label>Available Times for {format(phoneData.booking_date, 'MMM d')}</Label>
                        {loadingSlots ? (
                          <div className="flex justify-center py-4">
                            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                          </div>
                        ) : availableSlots.length > 0 ? (
                          <div className="grid grid-cols-4 gap-2 max-h-40 overflow-y-auto">
                            {availableSlots.map(slot => (
                              <Button
                                key={slot.time}
                                variant={phoneData.booking_time === slot.time ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setPhoneData({ ...phoneData, booking_time: slot.time })}
                                className="text-sm"
                                data-testid={`time-slot-${slot.time}`}
                              >
                                {slot.time}
                              </Button>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground text-center py-4">
                            No available times for this date
                          </p>
                        )}
                      </div>
                    )}
                    
                    <div className="flex gap-3 pt-2">
                      <Button variant="outline" onClick={() => setPhoneBookingStep(2)} className="flex-1">
                        Back
                      </Button>
                      <Button 
                        onClick={handlePhoneBookingSubmit} 
                        disabled={!phoneData.booking_time || submitting}
                        className="flex-1"
                        data-testid="phone-booking-submit"
                      >
                        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm Booking'}
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Step 4: Deposit Required - Collect Email */}
                {phoneBookingStep === 4 && (
                  <div className="space-y-4">
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <CreditCard className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-semibold text-amber-800">Deposit Required</p>
                          <p className="text-sm text-amber-700 mt-1">
                            A deposit of <strong>£{phoneDepositInfo?.amount?.toFixed(2) || '20.00'}</strong> is required for this booking.
                          </p>
                          <p className="text-sm text-amber-600 mt-2">
                            We'll send a secure payment link to the customer's email.
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-3 bg-slate-50 rounded-lg text-sm">
                      <p className="font-medium">{phoneData.customer_name}</p>
                      <p className="text-muted-foreground">
                        {format(phoneData.booking_date, 'EEEE, MMMM d, yyyy')} at {phoneData.booking_time}
                      </p>
                      <p className="text-muted-foreground">{phoneData.party_size} guests</p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-base font-semibold">Customer Email *</Label>
                      <p className="text-sm text-muted-foreground">Required to send the deposit payment link</p>
                      <Input
                        type="email"
                        value={phoneData.customer_email}
                        onChange={(e) => setPhoneData({ ...phoneData, customer_email: e.target.value })}
                        placeholder="customer@email.com"
                        className="text-lg"
                        autoFocus
                        data-testid="deposit-email-input"
                      />
                    </div>
                    
                    <div className="flex gap-3 pt-2">
                      <Button variant="outline" onClick={() => setPhoneBookingStep(3)} className="flex-1">
                        Back
                      </Button>
                      <Button 
                        onClick={sendDepositEmail} 
                        disabled={!phoneData.customer_email || !phoneData.customer_email.includes('@') || sendingDepositEmail}
                        className="flex-1 bg-amber-600 hover:bg-amber-700"
                        data-testid="send-deposit-link"
                      >
                        {sendingDepositEmail ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Mail className="h-4 w-4 mr-2" />
                            Send Deposit Link
                          </>
                        )}
                      </Button>
                    </div>
                    
                    <p className="text-xs text-center text-muted-foreground">
                      The booking will be held pending payment. Customer has 24 hours to complete payment.
                    </p>
                  </div>
                )}
              </DialogContent>
            </Dialog>
            </div>

            {viewMode === 'tables' && getUniqueZones().length > 0 && (
              <div className="-mx-1 flex max-w-full items-center gap-1 overflow-x-auto px-1 py-0.5 [scrollbar-width:none] sm:flex-wrap sm:overflow-visible [&::-webkit-scrollbar]:hidden">
                <span className="shrink-0 pl-1 text-xs font-medium text-slate-600">Zone</span>
                <Button
                  size="sm"
                  variant={selectedZone === 'all' ? 'default' : 'secondary'}
                  onClick={() => setSelectedZone('all')}
                  className="h-9 shrink-0 touch-manipulation px-3 text-xs"
                >
                  All
                </Button>
                {getUniqueZones().map((zone) => (
                  <Button
                    key={zone}
                    size="sm"
                    variant={selectedZone === zone ? 'default' : 'secondary'}
                    onClick={() => setSelectedZone(zone)}
                    className="h-9 shrink-0 touch-manipulation px-3 text-xs"
                  >
                    {zone}
                  </Button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Booking Dialog */}
      <Dialog
        open={editDialogOpen}
        onOpenChange={(open) => {
          setEditDialogOpen(open);
          if (!open) setEditTimeUnlocked(false);
        }}
      >
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Booking</DialogTitle>
            <DialogDescription>
              Update booking time, status, party size, and table assignment
            </DialogDescription>
          </DialogHeader>
          {editingBooking && (
            <div className="space-y-6">
              <BookingCreatorTag booking={editingBooking} />
              {editingBooking.staff_only && (
                <div className="rounded-lg border-2 border-violet-500 bg-violet-50 px-3 py-2 text-sm font-semibold text-violet-950">
                  Staff hold — not offered on your public booking site and does not count toward online slot capacity.
                </div>
              )}
              {/* Customer Info (read-only) */}
              <div className="p-3 bg-secondary/50 rounded-lg">
                <p className="font-medium">{editingBooking.customer_name}</p>
                <p
                  className="text-sm text-muted-foreground"
                  onDoubleClick={() => {
                    setEditTimeUnlocked(true);
                    toast.info('Time edit unlocked');
                  }}
                  title="Double tap to unlock time editing"
                >
                  {editingBooking.booking_time} • Originally {editingBooking.party_size} guests
                </p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label>Booking Time</Label>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 text-xs"
                    onClick={() => setEditTimeUnlocked((v) => !v)}
                  >
                    {editTimeUnlocked ? 'Lock time' : 'Adjust time'}
                  </Button>
                </div>
                {editTimeUnlocked ? (
                  <Select
                    value={editingBooking.booking_time}
                    onValueChange={(v) => setEditingBooking({ ...editingBooking, booking_time: v })}
                  >
                    <SelectTrigger data-testid="edit-booking-time">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {timeSlots.map((slot) => (
                        <SelectItem key={slot} value={slot}>
                          {slot}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Double tap the time above, or tap Adjust time, to move this booking to another slot.
                  </p>
                )}
              </div>

              {/* Allergies Alert */}
              {editingBooking.allergies && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Allergies / Dietary</p>
                    <p className="text-sm text-amber-700">{editingBooking.allergies}</p>
                  </div>
                </div>
              )}

              {/* Deposit Info Alert */}
              {editingBooking.deposit_status === 'paid' && editingBooking.deposit_amount > 0 && (
                <div className="p-4 bg-green-50 border-2 border-green-500 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <CreditCard className="h-5 w-5 text-green-600" />
                    <p className="font-bold text-green-800 text-lg">DEPOSIT PAID{depositMethodTag(editingBooking)}</p>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <p className="text-3xl font-bold text-green-700">
                      {CURRENCY_SYMBOLS[editingBooking.deposit_currency] || '£'}{editingBooking.deposit_amount.toFixed(2)}
                    </p>
                    <p className="text-sm text-green-600">to be deducted from final bill</p>
                  </div>
                  {editingBooking.deposit_locked && (
                    <p className="mt-2 flex items-center gap-1 text-sm font-semibold text-amber-800">
                      <Lock className="h-4 w-4" /> Locked: moved inside the non-refundable period, deposit kept
                    </p>
                  )}
                </div>
              )}

              {editingBooking.deposit_status === 'refunded' && (
                <div className="p-4 bg-purple-50 border-2 border-purple-400 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <RefreshCw className="h-5 w-5 text-purple-600" />
                    <p className="font-bold text-purple-800 text-lg">DEPOSIT REFUNDED</p>
                  </div>
                  <p className="text-2xl font-bold text-purple-700">
                    {CURRENCY_SYMBOLS[editingBooking.deposit_currency] || '£'}
                    {(editingBooking.deposit_refund_amount ?? editingBooking.deposit_amount ?? 0).toFixed(2)}
                  </p>
                </div>
              )}

              {isDepositUnpaid(editingBooking) && (
                <div className="p-4 bg-red-50 border-2 border-red-500 rounded-lg flex items-start gap-2" data-testid="edit-deposit-not-paid">
                  <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold text-red-800 text-lg">DEPOSIT NOT PAID</p>
                    <p className="text-sm text-red-700">
                      The customer has not paid their {formatDepositAmount(editingBooking)} deposit. Do not deduct a deposit from their bill.
                    </p>
                  </div>
                </div>
              )}

              {/* Phone Deposit — mark as paid manually */}
              {editingBooking.deposit_status !== 'paid' && editingBooking.deposit_required && (
                <div className="p-4 border border-slate-200 rounded-lg space-y-3 bg-slate-50">
                  <div className="flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-slate-600" />
                    <p className="font-medium text-slate-700 text-sm">Mark Deposit Paid (Phone / Cash)</p>
                  </div>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm font-medium">
                        {CURRENCY_SYMBOLS[editingBooking.deposit_currency] || '£'}
                      </span>
                      <Input
                        type="number"
                        min="0.01"
                        step="0.01"
                        placeholder={editingBooking.deposit_amount?.toFixed(2) || '0.00'}
                        value={phoneDepositAmount}
                        onChange={(e) => setPhoneDepositAmount(e.target.value)}
                        className="pl-7"
                      />
                    </div>
                    <Button
                      type="button"
                      onClick={handleMarkDepositPaid}
                      disabled={markingDepositPaid || !phoneDepositAmount}
                      className="bg-green-600 hover:bg-green-700 text-white whitespace-nowrap"
                    >
                      {markingDepositPaid ? 'Saving…' : 'Mark Paid'}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500">
                    Enter the amount received and tap Mark Paid to record it.
                  </p>
                </div>
              )}

              {/* Also show mark-paid option when no deposit was requested (ad-hoc phone deposit) */}
              {!editingBooking.deposit_required && editingBooking.deposit_status !== 'paid' && (
                <div className="p-4 border border-slate-200 rounded-lg space-y-3 bg-slate-50">
                  <div className="flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-slate-600" />
                    <p className="font-medium text-slate-700 text-sm">Record Phone / Cash Deposit</p>
                  </div>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm font-medium">£</span>
                      <Input
                        type="number"
                        min="0.01"
                        step="0.01"
                        placeholder="0.00"
                        value={phoneDepositAmount}
                        onChange={(e) => setPhoneDepositAmount(e.target.value)}
                        className="pl-7"
                      />
                    </div>
                    <Button
                      type="button"
                      onClick={handleMarkDepositPaid}
                      disabled={markingDepositPaid || !phoneDepositAmount}
                      className="bg-green-600 hover:bg-green-700 text-white whitespace-nowrap"
                    >
                      {markingDepositPaid ? 'Saving…' : 'Mark Paid'}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500">
                    Optionally record a deposit taken over the phone or in person.
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  Allergies / Dietary
                </Label>
                <Textarea
                  value={editingBooking.allergies || ''}
                  onChange={(e) => setEditingBooking({ ...editingBooking, allergies: e.target.value })}
                  placeholder="e.g., Nut allergy, Gluten-free"
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label>Special Requests</Label>
                <Textarea
                  value={editingBooking.special_requests || ''}
                  onChange={(e) => setEditingBooking({ ...editingBooking, special_requests: e.target.value })}
                  placeholder="e.g., Birthday table, quiet area, highchair"
                  rows={3}
                />
              </div>

              {/* Status - Quick Tap Buttons */}
              <div className="space-y-2">
                <Label>Status</Label>
                <div className="grid grid-cols-2 gap-2">
                  {STATUS_OPTIONS.map(opt => {
                    const isSelected = editingBooking.status === opt.value;
                    const statusColors = {
                      'confirmed': 'bg-blue-500 hover:bg-blue-600 text-white',
                      'arrived': 'bg-amber-500 hover:bg-amber-600 text-white',
                      'seated': 'bg-emerald-500 hover:bg-emerald-600 text-white',
                      'completed': 'bg-slate-500 hover:bg-slate-600 text-white',
                      'no-show': 'bg-gray-500 hover:bg-gray-600 text-white',
                      'cancelled': 'bg-red-500 hover:bg-red-600 text-white'
                    };
                    const statusIcons = {
                      'confirmed': <Check className="h-4 w-4" />,
                      'arrived': <UserCheck className="h-4 w-4" />,
                      'seated': <Users className="h-4 w-4" />,
                      'completed': <CheckCircle className="h-4 w-4" />,
                      'no-show': <UserX className="h-4 w-4" />,
                      'cancelled': <XCircle className="h-4 w-4" />
                    };
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setEditingBooking({ ...editingBooking, status: opt.value })}
                        className={cn(
                          "flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all",
                          isSelected 
                            ? `${statusColors[opt.value]} ring-2 ring-offset-2 ring-${opt.value === 'confirmed' ? 'blue' : opt.value === 'arrived' ? 'amber' : opt.value === 'seated' ? 'emerald' : opt.value === 'cancelled' ? 'red' : 'slate'}-500`
                            : "bg-slate-100 hover:bg-slate-200 text-slate-700"
                        )}
                        data-testid={`status-btn-${opt.value}`}
                      >
                        {statusIcons[opt.value]}
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Party Size */}
              <div className="space-y-2">
                <Label>Party Size</Label>
                <Select 
                  value={String(editingBooking.party_size)} 
                  onValueChange={(v) => setEditingBooking({ ...editingBooking, party_size: parseInt(v) })}
                >
                  <SelectTrigger data-testid="edit-party-size">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20].map(n => (
                      <SelectItem key={n} value={String(n)}>{n} guests</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Table Assignment */}
              {tables.length > 0 && (
                <div className="space-y-2">
                  <Label>Assigned tables</Label>
                  <p className="text-xs text-muted-foreground -mt-1 mb-1">
                    Click a table to <span className="font-medium text-foreground">move</span> the booking there.
                    Hold <kbd className="rounded border bg-muted px-1 py-0.5 font-mono text-[10px]">Shift</kbd> while clicking to{' '}
                    <span className="font-medium text-foreground">add</span> a second table (split large parties).
                    Tables stay reserved for the overlap window set for this weekday (Operating hours) or the
                    booking default, until completed, cancelled, or no-show.
                  </p>
                  <div className="grid grid-cols-4 gap-2">
                    {tables.filter(t => t.is_active !== false).map(table => {
                      const isAssigned = editingBooking.assigned_tables?.includes(table.id);
                      const isAvailable = tableAvailability[table.id] === true;
                      const isUnavailable = !isAssigned && !isAvailable;
                      
                      return (
                        <Button
                          key={table.id}
                          type="button"
                          variant={isAssigned ? 'default' : isUnavailable ? 'secondary' : 'outline'}
                          size="sm"
                          onClick={(e) => toggleTableAssignment(table.id, { addTable: e.shiftKey })}
                          disabled={isUnavailable}
                          className="flex flex-col h-auto py-2 relative"
                          data-testid={`assign-table-${table.id}`}
                        >
                          <span className="font-semibold">{table.name}</span>
                          <span className="text-xs opacity-70">{table.capacity} seats</span>
                          {isUnavailable && (
                            <span className="text-xs text-muted-foreground mt-1">Unavailable</span>
                          )}
                        </Button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Selected: {editingBooking.assigned_tables?.length || 0} table(s) • 
                    Total capacity: {tables
                      .filter(t => editingBooking.assigned_tables?.includes(t.id))
                      .reduce((sum, t) => sum + t.capacity, 0)} seats
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="sticky bottom-0 bg-background pt-3">
            {editingBooking && !['cancelled', 'no-show', 'completed'].includes(editingBooking.status) && editingBooking.deposit_status !== 'refunded' && (
              <Button
                variant="secondary"
                className="sm:mr-auto"
                onClick={() => { setMoveTarget(editingBooking); setEditDialogOpen(false); }}
                data-testid="tablet-move-booking"
              >
                <CalendarClock className="h-4 w-4 mr-2" />
                Move to another date
              </Button>
            )}
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleEditSave} disabled={savingEdit} data-testid="save-edit">
              {savingEdit ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MoveBookingDialog
        open={Boolean(moveTarget)}
        onOpenChange={(open) => { if (!open) setMoveTarget(null); }}
        booking={moveTarget}
        restaurantId={restaurant?.id}
        onMoved={() => { fetchDayBookings(); fetchCalendarData(); }}
      />

      {/* Deposit Link Share Dialog */}
      <Dialog open={depositLinkDialog.open} onOpenChange={(open) => setDepositLinkDialog({ ...depositLinkDialog, open })}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-amber-600" />
              Send Deposit Link to Customer
            </DialogTitle>
            <DialogDescription>
              Booking created! Share this payment link with <strong>{depositLinkDialog.customerName}</strong>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 pt-2">
            {/* Payment Link Box */}
            <div className="p-3 bg-slate-50 rounded-lg border">
              <Label className="text-xs text-muted-foreground mb-1 block">Payment Link</Label>
              <div className="flex gap-2">
                <Input 
                  value={depositLinkDialog.url} 
                  readOnly 
                  className="font-mono text-xs bg-white"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    navigator.clipboard.writeText(depositLinkDialog.url);
                    toast.success('Link copied!');
                  }}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Quick Share Options */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Quick Share</Label>
              <div className="grid grid-cols-2 gap-2">
                {/* WhatsApp */}
                {depositLinkDialog.customerPhone && (
                  <a
                    href={`https://wa.me/${depositLinkDialog.customerPhone.replace(/\D/g, '')}?text=${encodeURIComponent(
                      `Hi ${depositLinkDialog.customerName}! Please complete your booking deposit using this link: ${depositLinkDialog.url}`
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-green-500 hover:bg-green-600 text-white font-medium transition-colors"
                  >
                    <MessageSquare className="h-4 w-4" />
                    WhatsApp
                  </a>
                )}
                
                {/* SMS */}
                {depositLinkDialog.customerPhone && (
                  <a
                    href={`sms:${depositLinkDialog.customerPhone}?body=${encodeURIComponent(
                      `Hi ${depositLinkDialog.customerName}! Complete your booking deposit here: ${depositLinkDialog.url}`
                    )}`}
                    className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-medium transition-colors"
                  >
                    <Phone className="h-4 w-4" />
                    SMS
                  </a>
                )}
                
                {/* Email (manual) */}
                <a
                  href={`mailto:${depositLinkDialog.customerEmail}?subject=${encodeURIComponent(
                    `Complete Your Booking - Deposit Required`
                  )}&body=${encodeURIComponent(
                    `Hi ${depositLinkDialog.customerName},\n\nThank you for your booking request!\n\nTo secure your reservation, please complete the deposit payment using the link below:\n\n${depositLinkDialog.url}\n\nThank you!`
                  )}`}
                  className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-slate-600 hover:bg-slate-700 text-white font-medium transition-colors"
                >
                  <Mail className="h-4 w-4" />
                  Email
                </a>
                
                {/* Open Link */}
                <a
                  href={depositLinkDialog.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-amber-500 hover:bg-amber-600 text-white font-medium transition-colors"
                >
                  <ExternalLink className="h-4 w-4" />
                  Open Link
                </a>
              </div>
            </div>

            {/* Info Box */}
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 text-sm text-amber-800">
              <p className="font-medium">⏰ Booking held for 24 hours</p>
              <p className="mt-1 text-xs">The customer must complete payment within 24 hours or the booking will expire.</p>
            </div>

            {/* Note about email setup */}
            <p className="text-xs text-center text-muted-foreground">
              💡 Tip: Set up email in Settings → Email to automatically send deposit links
            </p>
          </div>

          <DialogFooter>
            <Button onClick={() => setDepositLinkDialog({ ...depositLinkDialog, open: false })} className="w-full">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Time Slot Action Dialog */}
      <Dialog open={timeSlotDialogOpen} onOpenChange={(open) => {
        setTimeSlotDialogOpen(open);
        if (!open) {
          setSlotsToBlock([]);
        } else if (selectedTimeSlot) {
          // Pre-select currently blocked slots
          setSlotsToBlock(getBlockedSlotsForTime(selectedTimeSlot));
        }
      }}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Manage Time: {selectedTimeSlot && (
                <>
                  {(() => {
                    if (!selectedTimeSlot) return '';
                    const [h, m] = selectedTimeSlot.split(':').map(Number);
                    const hour = h > 12 ? h - 12 : h === 0 ? 12 : h;
                    const ampm = h >= 12 ? 'PM' : 'AM';
                    return `${hour}:${String(m).padStart(2, '0')} ${ampm}`;
                  })()}
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              Add a booking or select slots to block for this time.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 pt-2">
            {/* Add Booking Button */}
            <Button 
              className="w-full justify-start gap-3" 
              onClick={handleAddBookingFromTimeline}
              data-testid="timeline-add-booking"
            >
              <Plus className="h-5 w-5" />
              Add Booking
            </Button>
            
            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-3 text-slate-700">Block Slots (prevents new bookings)</p>
              
              {/* Slot checkboxes */}
              <div className="flex flex-wrap gap-2 mb-3">
                {Array.from({ length: restaurant?.settings?.tables_per_slot || 4 }, (_, i) => i + 1).map((slotNum) => {
                  const timeBookings = bookings.filter(b => 
                    b.booking_time === selectedTimeSlot && 
                    !['cancelled', 'no-show'].includes(b.status)
                  );
                  const hasBookingInSlot = timeBookings[slotNum - 1];
                  
                  return (
                    <button
                      key={slotNum}
                      onClick={() => toggleSlotToBlock(slotNum)}
                      disabled={hasBookingInSlot}
                      className={cn(
                        "px-4 py-2 rounded-lg border-2 font-medium text-sm transition-all",
                        slotsToBlock.includes(slotNum)
                          ? "bg-red-500 border-red-500 text-white"
                          : "bg-white border-slate-200 text-slate-700 hover:border-red-300",
                        hasBookingInSlot && "opacity-50 cursor-not-allowed bg-blue-100 border-blue-300"
                      )}
                      title={hasBookingInSlot ? `Slot ${slotNum} has a booking` : `Block slot ${slotNum}`}
                    >
                      Slot {slotNum}
                      {hasBookingInSlot && " (Booked)"}
                    </button>
                  );
                })}
              </div>
              
              <div className="flex gap-2 mb-3">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={selectAllSlots}
                  className="text-xs"
                >
                  Select All
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setSlotsToBlock([])}
                  className="text-xs"
                >
                  Clear
                </Button>
              </div>
              
              {/* Block/Unblock buttons */}
              <div className="flex gap-2">
                {getBlockedSlotsForTime(selectedTimeSlot).length > 0 ? (
                  <Button 
                    variant="outline"
                    className="flex-1 gap-2 border-green-500 text-green-600 hover:bg-green-50"
                    onClick={handleUnblockTime}
                    disabled={blockingSlots}
                  >
                    {blockingSlots ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unlock className="h-4 w-4" />}
                    Unblock All
                  </Button>
                ) : null}
                <Button 
                  className={cn(
                    "flex-1 gap-2",
                    slotsToBlock.length > 0 ? "bg-red-500 hover:bg-red-600" : ""
                  )}
                  onClick={handleBlockSlots}
                  disabled={blockingSlots || slotsToBlock.length === 0}
                >
                  {blockingSlots ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ban className="h-4 w-4" />}
                  Block {slotsToBlock.length} Slot{slotsToBlock.length !== 1 ? 's' : ''}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick Walk-in / staff hold dialog (from timeline) */}
      <Dialog
        open={quickBookingDialogOpen}
        onOpenChange={(open) => {
          setQuickBookingDialogOpen(open);
          if (!open) setQuickBookingData(resetQuickBookingForm());
        }}
      >
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              {quickBookingData.is_staff_hold ? (
                <>
                  <UserPlus className="h-5 w-5 text-violet-700" />
                  Staff hold at{' '}
                  {quickBookingData.booking_time && (() => {
                    const [h, m] = quickBookingData.booking_time.split(':').map(Number);
                    const hour = h > 12 ? h - 12 : h === 0 ? 12 : h;
                    const ampm = h >= 12 ? 'PM' : 'AM';
                    return `${hour}:${String(m).padStart(2, '0')} ${ampm}`;
                  })()}
                </>
              ) : (
                <>
                  <UserCheck className="h-5 w-5 text-emerald-600" />
                  Add Walk-in at{' '}
                  {quickBookingData.booking_time && (() => {
                    const [h, m] = quickBookingData.booking_time.split(':').map(Number);
                    const hour = h > 12 ? h - 12 : h === 0 ? 12 : h;
                    const ampm = h >= 12 ? 'PM' : 'AM';
                    return `${hour}:${String(m).padStart(2, '0')} ${ampm}`;
                  })()}
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {format(selectedDate, 'EEEE, MMMM d, yyyy')}
              {quickBookingData.is_staff_hold ? (
                <span className="mt-2 block rounded-md border border-violet-300 bg-violet-50 px-3 py-2 text-xs font-medium text-violet-950">
                  This reservation does not use your public “tables per slot” limit and is hidden from the online booking widget.
                </span>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleQuickBookingSubmit} className="space-y-4">
            {/* Party Size - Most Important */}
            <div className="space-y-2">
              <Label className="text-base font-semibold">Number of Guests</Label>
              <div className="grid grid-cols-6 gap-2">
                {[1,2,3,4,5,6].map(n => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setQuickBookingData({ ...quickBookingData, party_size: String(n) })}
                    className={cn(
                      "h-12 rounded-lg font-bold text-lg transition-all",
                      quickBookingData.party_size === String(n)
                        ? "bg-primary text-primary-foreground ring-2 ring-primary ring-offset-2"
                        : "bg-slate-100 hover:bg-slate-200 text-slate-700"
                    )}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                {[7,8,10,12].map(n => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setQuickBookingData({ ...quickBookingData, party_size: String(n) })}
                    className={cn(
                      "flex-1 h-10 rounded-lg font-semibold transition-all",
                      quickBookingData.party_size === String(n)
                        ? "bg-primary text-primary-foreground ring-2 ring-primary ring-offset-2"
                        : "bg-slate-100 hover:bg-slate-200 text-slate-700"
                    )}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>

            {/* Allergies - Important for service */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-base font-semibold">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                Allergies / Dietary
              </Label>
              <Input
                value={quickBookingData.allergies}
                onChange={(e) => setQuickBookingData({ ...quickBookingData, allergies: e.target.value })}
                placeholder="e.g., Nut allergy, Gluten-free, Vegan..."
                className="border-amber-200 focus:border-amber-400"
                data-testid="quick-booking-allergies"
              />
            </div>

            {/* Table Assignment */}
            {tables.length > 0 && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-base font-semibold">
                  <Table2 className="h-4 w-4" />
                  Assign Table
                </Label>
                <div className="flex flex-wrap gap-2">
                  {tables.filter(t => t.is_active).map(table => {
                    const dateStr = format(selectedDate, 'yyyy-MM-dd');
                    const taken =
                      tableOverlapsActiveBooking(
                        table.id,
                        dateStr,
                        quickBookingData.booking_time,
                        null,
                        bookings,
                        null
                      ) && !(quickBookingData.assigned_tables || []).includes(table.id);
                    return (
                      <button
                        key={table.id}
                        type="button"
                        disabled={taken}
                        onClick={() => toggleQuickBookingTable(table.id)}
                        className={cn(
                          'rounded-lg px-3 py-2 text-sm font-medium transition-all',
                          (quickBookingData.assigned_tables || []).includes(table.id)
                            ? 'bg-emerald-500 text-white'
                            : taken
                              ? 'cursor-not-allowed bg-slate-200 text-slate-400'
                              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        )}
                      >
                        {table.name}
                        <span className="ml-1 text-xs opacity-70">({table.capacity})</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Optional: Name (collapsed by default) */}
            <details className="group">
              <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
                <ChevronRight className="h-4 w-4 transition-transform group-open:rotate-90" />
                Add name or contact (optional)
              </summary>
              <div className="mt-3 space-y-3 pl-5">
                <Input
                  value={quickBookingData.customer_name}
                  onChange={(e) => setQuickBookingData({ ...quickBookingData, customer_name: e.target.value })}
                  placeholder="Guest name"
                  data-testid="quick-booking-name"
                />
                <Input
                  value={quickBookingData.customer_phone}
                  onChange={(e) => setQuickBookingData({ ...quickBookingData, customer_phone: e.target.value })}
                  placeholder="Phone number"
                  data-testid="quick-booking-phone"
                />
              </div>
            </details>

            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setQuickBookingDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className={cn(
                  'flex-1',
                  quickBookingData.is_staff_hold
                    ? 'bg-violet-700 hover:bg-violet-800'
                    : 'bg-emerald-600 hover:bg-emerald-700'
                )}
                data-testid="quick-booking-submit"
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : quickBookingData.is_staff_hold ? (
                  <>
                    <UserPlus className="mr-2 h-4 w-4" />
                    Add staff hold
                  </>
                ) : (
                  <>
                    <UserCheck className="mr-2 h-4 w-4" />
                    Seat {quickBookingData.party_size} Guest{quickBookingData.party_size !== '1' ? 's' : ''}
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <div className="flex flex-col lg:flex-row lg:items-start">
        {/* Calendar sidebar: width shrinks when toggled off so the timeline can use full width */}
        <div
          className={cn(
            'hidden min-h-0 shrink-0 border-border/50 bg-white md:flex md:h-[calc(100dvh-11rem)] md:max-h-[calc(100dvh-11rem)] md:flex-col md:overflow-hidden md:border-b lg:sticky lg:top-[10.5rem] lg:z-30 lg:shrink-0 lg:border-b-0 lg:border-r transition-[width] duration-200 ease-out',
            showCalendar ? 'w-full md:w-full lg:w-80' : 'w-14 md:w-14 lg:w-14'
          )}
        >
          {showCalendar ? (
            <div className="flex h-full min-h-0 w-full flex-col">
              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pt-4">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <Label htmlFor="tablet-calendar-panel" className="text-sm font-medium text-slate-700">
                    Calendar
                  </Label>
                  <Switch
                    id="tablet-calendar-panel"
                    checked={showCalendar}
                    onCheckedChange={setShowCalendar}
                    className="touch-manipulation"
                    data-testid="toggle-calendar"
                  />
                </div>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => date && setSelectedDate(date)}
                  className="rounded-md"
                  modifiers={{
                    hasBookings: (date) => {
                      const dateStr = format(date, 'yyyy-MM-dd');
                      return calendarCounts[dateStr] > 0;
                    }
                  }}
                  modifiersClassNames={{
                    hasBookings: 'font-bold'
                  }}
                  components={{
                    DayContent: ({ date }) => {
                      const dateStr = format(date, 'yyyy-MM-dd');
                      const count = calendarCounts[dateStr];
                      return (
                        <div className="relative w-full h-full flex items-center justify-center">
                          {date.getDate()}
                          {count > 0 && (
                            <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary" />
                          )}
                        </div>
                      );
                    }
                  }}
                  data-testid="tablet-calendar"
                />

                {/* Quick Stats */}
                <div className="mt-6 space-y-3 pb-4">
                  <h3 className="font-medium text-sm text-muted-foreground">Today's Summary</h3>

                  {/* Period filter — only shown when there are 2+ named periods */}
                  {todayPeriods.length >= 2 && (
                    <div className="flex flex-wrap gap-1.5">
                      <button
                        onClick={() => setSelectedPeriod('all')}
                        className={`rounded-full px-3 py-1 text-xs font-medium transition-colors touch-manipulation ${
                          selectedPeriod === 'all'
                            ? 'bg-slate-900 text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        All
                      </button>
                      {todayPeriods.map(p => (
                        <button
                          key={p.title}
                          onClick={() => setSelectedPeriod(p.title)}
                          className={`rounded-full px-3 py-1 text-xs font-medium transition-colors touch-manipulation ${
                            selectedPeriod === p.title
                              ? 'bg-slate-900 text-white'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {p.title}
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-blue-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-blue-600">{expectedBookings}</p>
                      <div className="mt-1 inline-flex items-center justify-center gap-1 text-xs text-blue-600/80">
                        <Users className="h-3.5 w-3.5" />
                        <span>{expectedGuests}</span>
                      </div>
                      <p className="text-xs text-blue-600/80">Expected</p>
                    </div>
                    <div className="bg-emerald-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-emerald-600">{seatedBookings}</p>
                      <div className="mt-1 inline-flex items-center justify-center gap-1 text-xs text-emerald-600/80">
                        <Users className="h-3.5 w-3.5" />
                        <span>{seatedGuests}</span>
                      </div>
                      <p className="text-xs text-emerald-600/80">Seated</p>
                    </div>
                  </div>

                  {/* Tables Summary */}
                  {tables.length > 0 && (
                    <div className="pt-3 border-t">
                      <h3 className="font-medium text-sm text-muted-foreground mb-2">Tables</h3>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="bg-emerald-50 rounded-lg p-3 text-center">
                          <p className="text-2xl font-bold text-emerald-600">
                            {tables.filter(t => !bookingsByTable[t.id]?.some(b => ['arrived', 'seated'].includes(b.status))).length}
                          </p>
                          <p className="text-xs text-emerald-600/80">Available</p>
                        </div>
                        <div className="bg-amber-50 rounded-lg p-3 text-center">
                          <p className="text-2xl font-bold text-amber-600">
                            {tables.filter(t => bookingsByTable[t.id]?.some(b => ['arrived', 'seated'].includes(b.status))).length}
                          </p>
                          <p className="text-xs text-amber-600/80">Occupied</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Same layout as Bookings (AdminLayout sidebar footer) — bottom of menu column */}
              <div className="shrink-0 border-t border-border/50 bg-white p-4 shadow-[0_-6px_16px_-8px_rgba(0,0,0,0.08)]" data-testid="tablet-staff-dock">
                <div className="mb-3 rounded-lg border border-red-100 bg-red-50 px-3 py-2.5">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-red-800/80">Signed in as</p>
                  <p className="mt-0.5 text-lg font-semibold leading-tight text-red-950 truncate">
                    {user?.name || user?.email || 'Staff user'}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  className="w-full justify-start gap-3 text-muted-foreground hover:text-destructive touch-manipulation"
                  onClick={handleSwitchUser}
                  data-testid="tablet-switch-user"
                >
                  <LogOut className="h-5 w-5" />
                  Log Off
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex h-full w-12 flex-col items-center gap-3 py-4">
              <Switch
                checked={showCalendar}
                onCheckedChange={setShowCalendar}
                className="touch-manipulation"
                data-testid="toggle-calendar"
              />
              <span
                className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground [writing-mode:vertical-rl] rotate-180"
                aria-hidden
              >
                Calendar
              </span>
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="min-h-0 flex-1 min-w-0 p-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : viewMode === 'timeline' ? (
            /* Timeline Grid View - Slots as columns, Time intervals as rows */
            (() => {
              const settings = restaurant?.settings || {};
              const tablesPerSlot = settings.tables_per_slot || 4;
              const bookingInterval = settings.booking_interval_minutes || 30;

              // Use shared getTimeSlots() (05:00 through end of day) — do not duplicate 9–23 grid logic here.
              
              // Group bookings by time slot
              const getBookingsForTimeSlot = (time) => {
                return bookings.filter(b => 
                  b.booking_time === time && 
                  !['cancelled', 'no-show'].includes(b.status)
                );
              };

              const isStaffOnlyBooking = (b) => Boolean(b?.staff_only);

              const maxStaffAtAnyTime = Math.max(
                0,
                ...timeSlots.map((t) =>
                  getBookingsForTimeSlot(t).filter(isStaffOnlyBooking).length
                )
              );
              const staffSlotColumns = maxStaffAtAnyTime + 1;
              const extendedSlotNumbers = Array.from(
                { length: tablesPerSlot + staffSlotColumns },
                (_, i) => i + 1
              );
              
              // Get total guest count for a time slot
              const getGuestCountForTime = (time) => {
                const timeBookings = getBookingsForTimeSlot(time);
                return timeBookings.reduce((sum, b) => sum + (b.party_size || 0), 0);
              };
              
              // Check if specific slot is blocked
              const isSlotBlocked = (time, slotNum) => {
                const blocked = blockedTimes.find(b => b.time === time);
                if (!blocked) return false;
                return blocked.blocked_slot_numbers.includes(slotNum);
              };
              
              // Format time for display
              const formatTimeDisplay = (time) => {
                const [h, m] = time.split(':').map(Number);
                const hour = h > 12 ? h - 12 : h === 0 ? 12 : h;
                const ampm = h >= 12 ? 'PM' : 'AM';
                return `${hour}:${String(m).padStart(2, '0')} ${ampm}`;
              };
              
              // Handle slot click (public slot grid — walk-in / seated flow)
              const handleSlotClick = (time) => {
                setQuickBookingData({
                  customer_name: '',
                  customer_email: '',
                  customer_phone: '',
                  party_size: '2',
                  allergies: '',
                  booking_time: time,
                  assigned_tables: [],
                  is_staff_hold: false
                });
                setQuickBookingDialogOpen(true);
              };

              const handleStaffHoldClick = (time) => {
                setQuickBookingData({
                  customer_name: '',
                  customer_email: '',
                  customer_phone: '',
                  party_size: '2',
                  allergies: '',
                  booking_time: time,
                  assigned_tables: [],
                  is_staff_hold: true
                });
                setQuickBookingDialogOpen(true);
              };
              
              // Handle long press on time label
              let longPressTimer = null;
              const handleTimeLongPressStart = (time) => {
                longPressTimer = setTimeout(() => {
                  setSelectedTimeSlot(time);
                  setTimeSlotDialogOpen(true);
                }, 500); // 500ms for long press
              };
              
              const handleTimeLongPressEnd = () => {
                if (longPressTimer) {
                  clearTimeout(longPressTimer);
                  longPressTimer = null;
                }
              };
              
              return (
                <div className="h-[calc(100dvh-13rem)] overflow-auto bg-white rounded-lg border shadow-sm sm:h-[calc(100vh-120px)]">
                  {/* Grid Header - Slot numbers */}
                  <div className="sticky top-0 z-20 bg-white border-b">
                    <div className="flex">
                      <div className="w-28 flex-shrink-0 p-3 border-r bg-slate-50 font-semibold text-sm text-slate-600">
                        Time
                      </div>
                      {extendedSlotNumbers.map((slotNum) => (
                        <div
                          key={slotNum}
                          className="flex-1 min-w-[120px] border-r bg-slate-50 p-3 text-center text-sm font-semibold text-slate-600"
                        >
                          Slot {slotNum}
                          {slotNum > tablesPerSlot && (
                            <div className="mt-0.5 text-[10px] font-medium leading-tight text-slate-500">
                              Staff only
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Grid Body - Time rows */}
                  <div>
                    {timeSlots.map((time) => {
                      const timeBookings = getBookingsForTimeSlot(time);
                      const publicSlotBookings = timeBookings.filter((b) => !isStaffOnlyBooking(b));
                      const staffOnlyBookings = timeBookings.filter((b) => isStaffOnlyBooking(b));
                      const guestCount = getGuestCountForTime(time);
                      
                      // Check if this is the current time slot (for highlighting)
                      const now = new Date();
                      const [slotH, slotM] = time.split(':').map(Number);
                      const slotMinutes = slotH * 60 + slotM;
                      const currentMinutes = now.getHours() * 60 + now.getMinutes();
                      const isCurrentSlot = format(selectedDate, 'yyyy-MM-dd') === format(now, 'yyyy-MM-dd') && 
                        currentMinutes >= slotMinutes && currentMinutes < slotMinutes + bookingInterval;
                      
                      return (
                        <div 
                          key={time} 
                          data-time={time}
                          className={cn(
                            "flex border-b",
                            isCurrentSlot && "bg-blue-50/50"
                          )}
                        >
                          {/* Time label with guest count - long press to block */}
                          <div 
                            className={cn(
                              "w-28 flex-shrink-0 p-2 border-r flex flex-col items-center justify-center cursor-pointer select-none",
                              isCurrentSlot ? "bg-blue-100 font-semibold text-blue-700" : "bg-slate-50 hover:bg-slate-100"
                            )}
                            onMouseDown={() => handleTimeLongPressStart(time)}
                            onMouseUp={handleTimeLongPressEnd}
                            onMouseLeave={handleTimeLongPressEnd}
                            onTouchStart={() => handleTimeLongPressStart(time)}
                            onTouchEnd={handleTimeLongPressEnd}
                            title="Long press to block/unblock this time"
                          >
                            <div className="flex items-center">
                              <span className="text-xs font-medium">
                                {formatTimeDisplay(time)}
                              </span>
                              {isCurrentSlot && (
                                <span className="ml-1 w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                              )}
                            </div>
                            {/* Guest count */}
                            {guestCount > 0 && (
                              <div className="flex items-center gap-1 mt-1 text-xs text-slate-500">
                                <Users className="h-3 w-3" />
                                <span className="font-semibold">{guestCount}</span>
                              </div>
                            )}
                          </div>
                          
                          {/* Slot cells: public slots 1..N, then Slot N+1… as staff-only (same layout as public) */}
                          {extendedSlotNumbers.map((slotNum) => {
                            const statusColors = {
                              confirmed: 'bg-blue-500',
                              arrived: 'bg-amber-500',
                              seated: 'bg-emerald-500',
                              completed: 'bg-slate-400',
                            };

                            if (slotNum <= tablesPerSlot) {
                              const slotIndex = slotNum - 1;
                              const booking = publicSlotBookings[slotIndex];
                              const hasBooking = !!booking;
                              const slotBlocked = isSlotBlocked(time, slotNum);

                              if (slotBlocked && !hasBooking) {
                                return (
                                  <div
                                    key={slotNum}
                                    className="flex min-h-[60px] flex-1 min-w-[120px] cursor-pointer items-center justify-center border-r bg-red-50 p-1 hover:bg-red-100"
                                    onClick={() => {
                                      setSelectedTimeSlot(time);
                                      setTimeSlotDialogOpen(true);
                                    }}
                                  >
                                    <div className="flex items-center gap-1 text-xs text-red-600">
                                      <Ban className="h-3 w-3" />
                                      <span>Blocked</span>
                                    </div>
                                  </div>
                                );
                              }

                              if (hasBooking) {
                                const currencySymbol = CURRENCY_SYMBOLS[booking.deposit_currency] || '£';
                                return (
                                  <div
                                    key={slotNum}
                                    className={cn(
                                      'flex-1 min-w-[120px] cursor-pointer border-r p-1 transition-all hover:opacity-80',
                                      statusColors[booking.status] || 'bg-blue-500'
                                    )}
                                    onClick={() => openEditDialog(booking)}
                                    data-testid={`grid-booking-${booking.id}`}
                                  >
                                    <div className="h-full rounded p-1.5 text-white">
                                      <div className="truncate text-sm font-semibold">{booking.customer_name}</div>
                                      <div className="mt-0.5 flex items-center gap-1 text-xs opacity-90">
                                        <Users className="h-3 w-3" />
                                        {booking.party_size}
                                        <span className="ml-1 capitalize">{booking.status}</span>
                                      </div>
                                      {(() => {
                                        const tbl = getAssignedTablesText(booking, tables);
                                        return (
                                          <div
                                            className="mt-1 flex items-start gap-1 text-[10px] font-semibold leading-tight text-white/95"
                                            title={
                                              Array.isArray(booking.assigned_tables)
                                                ? booking.assigned_tables.join(', ')
                                                : undefined
                                            }
                                          >
                                            <Table2 className="mt-0.5 h-3 w-3 shrink-0 opacity-90" />
                                            <span className="break-words">{tbl || 'No table assigned'}</span>
                                          </div>
                                        );
                                      })()}
                                      <div
                                        className={cn(
                                          'mt-1 rounded px-1.5 py-1 text-[10px] font-bold leading-snug',
                                          booking.allergies
                                            ? 'bg-amber-200 text-amber-950 ring-1 ring-amber-500/90'
                                            : 'bg-white/20 text-white/85'
                                        )}
                                      >
                                        {booking.allergies ? (
                                          <span className="flex items-start gap-1">
                                            <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                                            <span className="break-words">Allergies: {booking.allergies}</span>
                                          </span>
                                        ) : (
                                          <span>Allergies: none noted</span>
                                        )}
                                      </div>
                                      {booking.special_requests && (
                                        <div className="mt-1 rounded px-1.5 py-1 text-[10px] leading-snug bg-blue-100 text-blue-900">
                                          <span className="flex items-start gap-1">
                                            <MessageSquare className="mt-0.5 h-3 w-3 shrink-0" />
                                            <span className="break-words">{booking.special_requests}</span>
                                          </span>
                                        </div>
                                      )}
                                      {booking.deposit_status === 'paid' && booking.deposit_amount > 0 && (
                                        <div className="mt-1 flex items-center gap-1 rounded bg-green-500 px-1.5 py-0.5 text-xs font-bold text-white shadow-sm">
                                          <CreditCard className="h-3 w-3 flex-shrink-0" />
                                          <span>
                                            Deposit: {currencySymbol}
                                            {booking.deposit_amount.toFixed(2)}
                                            {depositMethodTag(booking)}
                                          </span>
                                        </div>
                                      )}
                                      {booking.deposit_status === 'refunded' && (
                                        <div className="mt-1 flex items-center gap-1 rounded bg-purple-600 px-1.5 py-0.5 text-xs font-bold text-white shadow-sm">
                                          <RefreshCw className="h-3 w-3 flex-shrink-0" />
                                          <span>Refunded</span>
                                        </div>
                                      )}
                                      {isDepositUnpaid(booking) && (
                                    <div className="mt-1 flex items-center gap-1 rounded bg-red-600 px-1.5 py-0.5 text-xs font-bold text-white shadow-sm" data-testid="deposit-not-paid">
                                      <AlertTriangle className="h-3 w-3 flex-shrink-0" />
                                      <span>DEPOSIT NOT PAID</span>
                                    </div>
                                  )}
                                    </div>
                                  </div>
                                );
                              }

                              const isOpen = isOperatingHours(time);
                              return (
                                <div
                                  key={slotNum}
                                  className={cn(
                                    'group flex-1 min-w-[120px] cursor-pointer border-r p-1 transition-colors',
                                    isOpen ? 'bg-white hover:bg-emerald-50' : 'bg-slate-100 hover:bg-slate-200'
                                  )}
                                  onClick={() => handleSlotClick(time)}
                                  data-testid={`grid-slot-${time}-${slotNum}`}
                                >
                                  {isOpen ? (
                                    <div className="flex min-h-[60px] h-full items-center justify-center rounded border-2 border-dashed border-slate-200 transition-colors group-hover:border-emerald-400 group-hover:bg-emerald-50">
                                      <div className="flex flex-col items-center text-emerald-600 opacity-0 transition-opacity group-hover:opacity-100">
                                        <Plus className="h-5 w-5" />
                                        <span className="text-xs font-medium">Add</span>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="flex min-h-[60px] h-full items-center justify-center rounded">
                                      <div className="flex flex-col items-center text-slate-400">
                                        <Clock className="mb-1 h-4 w-4 opacity-60" />
                                        <span className="text-xs font-medium">CLOSED</span>
                                        <span className="mt-1 text-xs font-semibold text-emerald-600 opacity-0 transition-opacity group-hover:opacity-100">
                                          Click for VIP
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            }

                            const staffIdx = slotNum - tablesPerSlot - 1;
                            if (staffIdx > staffOnlyBookings.length) {
                              return (
                                <div
                                  key={slotNum}
                                  className="flex-1 min-w-[120px] border-r border-slate-100 bg-slate-50/40 p-1"
                                >
                                  <div className="min-h-[60px] rounded" />
                                </div>
                              );
                            }

                            if (staffIdx === staffOnlyBookings.length) {
                              const isOpen = isOperatingHours(time);
                              return (
                                <div
                                  key={slotNum}
                                  className={cn(
                                    'group flex-1 min-w-[120px] cursor-pointer border-r border-dashed border-slate-200/80 p-1 transition-colors',
                                    isOpen ? 'bg-white hover:bg-emerald-50/80' : 'bg-slate-100 hover:bg-slate-200'
                                  )}
                                  onClick={() => handleStaffHoldClick(time)}
                                  title="Staff hold — not on public booking"
                                  data-testid={`grid-staff-add-${time}-${slotNum}`}
                                >
                                  {isOpen ? (
                                    <div className="flex min-h-[60px] h-full items-center justify-center rounded border-2 border-dashed border-slate-200 transition-colors group-hover:border-emerald-400 group-hover:bg-emerald-50">
                                      <div className="flex flex-col items-center text-emerald-600 opacity-0 transition-opacity group-hover:opacity-100">
                                        <Plus className="h-5 w-5" />
                                        <span className="text-xs font-medium">Add staff</span>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="flex min-h-[60px] h-full items-center justify-center rounded">
                                      <div className="flex flex-col items-center text-slate-400">
                                        <Clock className="mb-1 h-4 w-4 opacity-60" />
                                        <span className="text-xs font-medium">CLOSED</span>
                                        <span className="mt-1 text-xs font-semibold text-emerald-600 opacity-0 transition-opacity group-hover:opacity-100">
                                          Add staff
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            }

                            const sb = staffOnlyBookings[staffIdx];
                            const currencySymbol = CURRENCY_SYMBOLS[sb.deposit_currency] || '£';
                            return (
                              <div
                                key={slotNum}
                                className={cn(
                                  'flex-1 min-w-[120px] cursor-pointer border-r p-1 transition-all hover:opacity-80',
                                  statusColors[sb.status] || 'bg-blue-500'
                                )}
                                onClick={() => openEditDialog(sb)}
                                data-testid={`grid-staff-booking-${sb.id}`}
                              >
                                <div className="h-full rounded p-1.5 text-white">
                                  <div className="text-[10px] font-bold uppercase tracking-wide opacity-90">
                                    Staff · not public
                                  </div>
                                  <div className="truncate text-sm font-semibold">{sb.customer_name}</div>
                                  <div className="mt-0.5 flex items-center gap-1 text-xs opacity-90">
                                    <Users className="h-3 w-3" />
                                    {sb.party_size}
                                    <span className="ml-1 capitalize">{sb.status}</span>
                                  </div>
                                  {(() => {
                                    const tbl = getAssignedTablesText(sb, tables);
                                    return (
                                      <div
                                        className="mt-1 flex items-start gap-1 text-[10px] font-semibold leading-tight text-white/95"
                                        title={
                                          Array.isArray(sb.assigned_tables) ? sb.assigned_tables.join(', ') : undefined
                                        }
                                      >
                                        <Table2 className="mt-0.5 h-3 w-3 shrink-0 opacity-90" />
                                        <span className="break-words">{tbl || 'No table assigned'}</span>
                                      </div>
                                    );
                                  })()}
                                  <div
                                    className={cn(
                                      'mt-1 rounded px-1.5 py-1 text-[10px] font-bold leading-snug',
                                      sb.allergies
                                        ? 'bg-amber-200 text-amber-950 ring-1 ring-amber-500/90'
                                        : 'bg-white/20 text-white/85'
                                    )}
                                  >
                                    {sb.allergies ? (
                                      <span className="flex items-start gap-1">
                                        <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                                        <span className="break-words">Allergies: {sb.allergies}</span>
                                      </span>
                                    ) : (
                                      <span>Allergies: none noted</span>
                                    )}
                                  </div>
                                  {sb.special_requests && (
                                    <div className="mt-1 rounded px-1.5 py-1 text-[10px] leading-snug bg-blue-100 text-blue-900">
                                      <span className="flex items-start gap-1">
                                        <MessageSquare className="mt-0.5 h-3 w-3 shrink-0" />
                                        <span className="break-words">{sb.special_requests}</span>
                                      </span>
                                    </div>
                                  )}
                                  {sb.deposit_status === 'paid' && sb.deposit_amount > 0 && (
                                    <div className="mt-1 flex items-center gap-1 rounded bg-green-500 px-1.5 py-0.5 text-xs font-bold text-white shadow-sm">
                                      <CreditCard className="h-3 w-3 flex-shrink-0" />
                                      <span>
                                        Deposit: {currencySymbol}
                                        {sb.deposit_amount.toFixed(2)}
                                        {depositMethodTag(sb)}
                                      </span>
                                    </div>
                                  )}
                                  {sb.deposit_status === 'refunded' && (
                                    <div className="mt-1 flex items-center gap-1 rounded bg-purple-600 px-1.5 py-0.5 text-xs font-bold text-white shadow-sm">
                                      <RefreshCw className="h-3 w-3 flex-shrink-0" />
                                      <span>Refunded</span>
                                    </div>
                                  )}
                                  {isDepositUnpaid(sb) && (
                                    <div className="mt-1 flex items-center gap-1 rounded bg-red-600 px-1.5 py-0.5 text-xs font-bold text-white shadow-sm" data-testid="deposit-not-paid">
                                      <AlertTriangle className="h-3 w-3 flex-shrink-0" />
                                      <span>DEPOSIT NOT PAID</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Summary Footer */}
                  <div className="sticky bottom-0 z-20 bg-slate-100 border-t p-3">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between text-sm">
                      <div className="flex flex-wrap items-center gap-4">
                        <span className="text-slate-600">
                          <strong>{bookings.filter(b => !['cancelled', 'no-show'].includes(b.status)).length}</strong> bookings today
                        </span>
                        <span className="text-slate-600">
                          <strong>{bookings.filter(b => !['cancelled', 'no-show'].includes(b.status)).reduce((sum, b) => sum + (b.party_size || 0), 0)}</strong> total guests
                        </span>
                        <span className="text-slate-600">
                          <strong>{tablesPerSlot}</strong> public slots ·{' '}
                          <strong>{extendedSlotNumbers.length - tablesPerSlot}</strong> staff columns (Slot{' '}
                          {tablesPerSlot + 1}+)
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 rounded bg-blue-500"></div>
                          <span className="text-xs text-slate-600">Confirmed</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 rounded bg-amber-500"></div>
                          <span className="text-xs text-slate-600">Arrived</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 rounded bg-emerald-500"></div>
                          <span className="text-xs text-slate-600">Seated</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 rounded bg-orange-500"></div>
                          <span className="text-xs text-slate-600">Allergy</span>
                        </div>
                        <span className="text-xs text-slate-500">
                          Staff slots are labelled in the header; they are not offered online.
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()
          ) : (
            /* Tables View */
            <ScrollArea className="h-[calc(100dvh-13rem)] sm:h-[calc(100vh-120px)]">
              <div className="space-y-4 px-0 sm:space-y-6">
                {/* Unassigned Bookings */}
                {unassignedBookings.length > 0 && (
                  <div className="space-y-2">
                    <div>
                      <h3 className="font-heading text-lg font-semibold flex items-center gap-2 text-amber-600">
                        <AlertTriangle className="h-5 w-5 shrink-0" />
                        Unassigned ({unassignedBookings.length})
                      </h3>
                      <p className="mt-1 text-xs sm:text-sm text-muted-foreground leading-snug">
                        Tap <span className="font-medium text-violet-700">Smart assign</span> for the suggested free table, or <span className="font-medium">Edit</span> to pick manually.
                      </p>
                    </div>
                    {/* Fixed height + native overflow so touch/wheel scroll works inside the page ScrollArea */}
                    <div
                      className="h-[min(26rem,52dvh)] min-h-[12rem] touch-pan-y overflow-y-auto overscroll-y-contain rounded-xl border border-amber-200/80 bg-amber-50/30 p-2 [scrollbar-gutter:stable]"
                      style={{ WebkitOverflowScrolling: 'touch' }}
                    >
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                        {unassignedBookings.map((booking) => {
                          const ranked = suggestTablesForBooking(booking);
                          const top = ranked[0];
                          return (
                            <BookingCard
                              key={booking.id}
                              booking={booking}
                              tables={tables}
                              getStatusBadge={getStatusBadge}
                              updateStatus={updateStatus}
                              onEdit={() => openEditDialog(booking)}
                              denseGrid
                              smartAssignHint={
                                top
                                  ? `Table ${top.name} (${top.capacity} seats)`
                                  : 'No free table at this time — use Edit to choose a table, or try another time.'
                              }
                              onSmartAssignTable={
                                top ? () => handleSmartAssignTableForBooking(booking) : undefined
                              }
                            />
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* Tables Grid */}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 sm:gap-4">
                  {getFilteredTables().filter(t => t.is_active !== false).map(table => {
                    const tableBookings = bookingsByTable[table.id] || [];
                    const isOccupied = tableBookings.some(b => ['arrived', 'seated'].includes(b.status));
                    const primaryBooking = pickPrimaryBookingForTable(tableBookings);

                    return (
                      <Card 
                        key={table.id}
                        className={cn(
                          "min-h-[112px] touch-manipulation border-2 transition-all cursor-pointer hover:shadow-md active:scale-[0.99]",
                          isOccupied ? "border-amber-400 bg-amber-50/50" : "border-emerald-400 bg-emerald-50/50"
                        )}
                        onClick={() => {
                          if (isOccupied && primaryBooking) {
                            openEditDialog(primaryBooking);
                          } else {
                            openTableDetailDialog(table);
                          }
                        }}
                        data-testid={`table-card-${table.id}`}
                      >
                        <CardHeader className="pb-2 px-4 pt-4 sm:px-6">
                          <div className="flex items-center justify-between">
                            <CardTitle className="font-heading text-lg flex items-center gap-2">
                              <Table2 className="h-5 w-5" />
                              Table {table.name}
                            </CardTitle>
                            <Badge variant={isOccupied ? 'default' : 'secondary'} className={isOccupied ? 'bg-amber-500' : 'bg-emerald-500'}>
                              {isOccupied ? 'Occupied' : 'Available'}
                            </Badge>
                          </div>
                          <CardDescription className="flex items-center gap-2">
                            <Users className="h-3 w-3" />
                            {table.capacity} seats
                            {table.zone && (
                              <span className="ml-2 px-2 py-0.5 bg-slate-200 text-slate-700 rounded text-xs">
                                {table.zone}
                              </span>
                            )}
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-2 px-4 pb-4 sm:px-6">
                          {tableBookings.length === 0 ? (
                            <div className="text-sm text-center py-4">
                              <p className="text-muted-foreground mb-2">No bookings on this table yet today</p>
                              <Button size="sm" variant="outline" className="h-10 gap-1 touch-manipulation" onClick={(e) => {
                                e.stopPropagation();
                                openTableDetailDialog(table);
                              }}>
                                <LayoutGrid className="h-3 w-3" />
                                View table / assign
                              </Button>
                            </div>
                          ) : (
                            tableBookings.map(booking => (
                              <div 
                                key={booking.id}
                                className="p-3 bg-white rounded-lg border shadow-sm"
                              >
                                <div className="flex items-center justify-between mb-1">
                                  <span className="font-medium">{booking.customer_name}</span>
                                  <Badge className={getStatusBadge(booking.status)} variant="secondary">
                                    {booking.status}
                                  </Badge>
                                </div>
                                <div className="text-sm text-muted-foreground flex items-center gap-3">
                                  <span className="flex items-center gap-1">
                                    <Clock className="h-3 w-3" />
                                    {booking.booking_time}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Users className="h-3 w-3" />
                                    {booking.party_size}
                                  </span>
                                </div>
                                {booking.allergies && (
                                  <div className="mt-2 p-2 bg-amber-100 rounded text-xs text-amber-800 flex items-center gap-1">
                                    <AlertTriangle className="h-3 w-3" />
                                    {booking.allergies}
                                  </div>
                                )}
                                {booking.special_requests && (
                                  <div className="mt-1 p-2 bg-blue-50 rounded text-xs text-blue-800 flex items-start gap-1">
                                    <MessageSquare className="h-3 w-3 shrink-0 mt-0.5" />
                                    {booking.special_requests}
                                  </div>
                                )}
                                {booking.deposit_status === 'paid' && booking.deposit_amount > 0 && (
                                  <div className="mt-2 p-2 bg-green-100 border border-green-300 rounded text-xs text-green-800 flex items-center gap-1 font-semibold">
                                    <CreditCard className="h-3 w-3" />
                                    Deposit: {CURRENCY_SYMBOLS[booking.deposit_currency] || '£'}{booking.deposit_amount.toFixed(2)} PAID{depositMethodTag(booking)}
                                  </div>
                                )}
                                {booking.deposit_status === 'refunded' && (
                                  <div className="mt-2 p-2 bg-purple-100 border border-purple-300 rounded text-xs text-purple-800 flex items-center gap-1 font-semibold">
                                    <RefreshCw className="h-3 w-3" />
                                    Deposit Refunded
                                  </div>
                                )}
                                {isDepositUnpaid(booking) && (
                                  <div className="mt-2 p-2 bg-red-600 rounded text-xs text-white flex items-center gap-1 font-bold">
                                    <AlertTriangle className="h-3 w-3" />
                                    DEPOSIT NOT PAID ({formatDepositAmount(booking)})
                                  </div>
                                )}
                                <div className="mt-2 flex gap-1">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="flex-1"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openEditDialog(booking);
                                    }}
                                  >
                                    <Edit2 className="h-3 w-3 mr-1" />
                                    Edit
                                  </Button>
                                  {booking.status === 'seated' && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        updateStatus(booking.id, 'completed');
                                      }}
                                    >
                                      <Check className="h-3 w-3 mr-1" />
                                      Done
                                    </Button>
                                  )}
                                </div>
                              </div>
                            ))
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            </ScrollArea>
          )}
        </div>
      </div>

      <Dialog
        open={assignDialogOpen}
        onOpenChange={(open) => {
          setAssignDialogOpen(open);
          if (!open) setSelectedTableForAssign(null);
        }}
      >
        <DialogContent className="flex max-h-[min(88dvh,720px)] w-[calc(100vw-1.25rem)] max-w-lg flex-col gap-3 overflow-hidden p-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:max-h-[min(90vh,720px)] sm:gap-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 pr-6 text-left">
              <Table2 className="h-5 w-5 shrink-0" />
              Table {selectedTableForAssign?.name}
            </DialogTitle>
            <DialogDescription className="space-y-1 text-left">
              {selectedTableForAssign && (
                <>
                  <span className="block">
                    {selectedTableForAssign.capacity} seats
                    {selectedTableForAssign.zone ? ` · ${selectedTableForAssign.zone}` : ''}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Who is on this table today. Pick an unassigned guest below to seat them here, or add a walk-in.
                  </span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <Button
            type="button"
            variant="outline"
            className="h-12 w-full touch-manipulation gap-2 border-violet-200 bg-violet-50/80 text-violet-900 hover:bg-violet-100 sm:h-11"
            onClick={() => {
              if (!selectedTableForAssign) return;
              setWalkinPlacement({
                smartPlace: true,
                preferTableId: selectedTableForAssign.id
              });
              setAssignDialogOpen(false);
              setSelectedTableForAssign(null);
              setWalkinDialogOpen(true);
            }}
          >
            <Plus className="h-4 w-4" />
            Walk-in for this table (smart place)
          </Button>

          <div className="space-y-2 min-h-0">
            <h4 className="text-sm font-semibold text-foreground">On this table today</h4>
            <ScrollArea className="max-h-[200px] rounded-md border p-2">
              {!selectedTableForAssign?.id ? null : getBookingsForTableSorted(selectedTableForAssign.id).length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">No bookings assigned to this table for this day.</p>
              ) : (
                <ul className="space-y-2">
                  {getBookingsForTableSorted(selectedTableForAssign.id).map((b) => (
                    <li
                      key={b.id}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-slate-50 px-2 py-1.5 text-sm"
                    >
                      <span className="font-medium">{b.customer_name}</span>
                      <span className="flex items-center gap-2 text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        {b.booking_time}
                        <Users className="h-3 w-3" />
                        {b.party_size}
                      </span>
                      <Badge variant="secondary" className={getStatusBadge(b.status)}>
                        {b.status}
                      </Badge>
                    </li>
                  ))}
                </ul>
              )}
            </ScrollArea>
          </div>

          <div className="space-y-2 min-h-0 flex-1 flex flex-col">
            <h4 className="text-sm font-semibold">Unassigned guests</h4>
            <p className="text-xs text-muted-foreground">
              Assign one of these guests to Table {selectedTableForAssign?.name}. For automatic table choice, use Smart assign on the guest in the list above.
            </p>
            <div className="min-h-[220px] max-h-[46vh] overflow-y-auto overscroll-contain pr-2">
              {unassignedBookings.length === 0 ? (
                <div className="text-center py-6 text-sm text-muted-foreground">
                  No unassigned bookings for this day.
                </div>
              ) : (
                <div className="space-y-3">
                  {unassignedBookings.map((ub) => {
                    const fitsThis =
                      selectedTableForAssign &&
                      parseInt(ub.party_size, 10) <= selectedTableForAssign.capacity &&
                      !tableOverlapsActiveBooking(
                        selectedTableForAssign.id,
                        format(selectedDate, 'yyyy-MM-dd'),
                        ub.booking_time,
                        ub.id,
                        bookings,
                        null
                      );
                    return (
                      <Card
                        key={ub.id}
                        className={cn(
                          'border-l-4 transition-all',
                          fitsThis ? 'border-l-blue-500' : 'border-l-slate-300 opacity-90'
                        )}
                      >
                        <CardContent className="p-3 space-y-2">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <div className="font-semibold">{ub.customer_name}</div>
                              <div className="text-xs text-muted-foreground flex flex-wrap gap-2 mt-1">
                                <span className="flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  {ub.booking_time}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Users className="h-3 w-3" />
                                  {ub.party_size} guests
                                </span>
                              </div>
                            </div>
                            <Badge variant="secondary">{ub.status}</Badge>
                          </div>
                          {ub.allergies && (
                            <div className="text-xs p-2 bg-amber-100 rounded flex items-center gap-1">
                              <AlertTriangle className="h-3 w-3 shrink-0" />
                              {ub.allergies}
                            </div>
                          )}
                          {ub.special_requests && (
                            <div className="text-xs p-2 bg-blue-50 rounded flex items-start gap-1">
                              <MessageSquare className="h-3 w-3 shrink-0 mt-0.5" />
                              {ub.special_requests}
                            </div>
                          )}
                          {!fitsThis && selectedTableForAssign && (
                            <p className="text-xs text-red-700">
                              {parseInt(ub.party_size, 10) > selectedTableForAssign.capacity
                                ? `Party larger than ${selectedTableForAssign.capacity} seats.`
                                : 'This table overlaps another active reservation for that period.'}
                            </p>
                          )}
                          {fitsThis && (
                            <Button
                              size="sm"
                              variant="secondary"
                              className="w-full"
                              type="button"
                              onClick={() => handleAssignBookingToSelectedTable(ub)}
                            >
                              <UserPlus className="h-3 w-3 mr-1" />
                              Assign to Table {selectedTableForAssign?.name}
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mobile / narrow: sidebar is hidden — full-width staff strip at bottom */}
      <div
        className="fixed bottom-0 left-0 right-0 z-50 flex items-stretch gap-2 border-t border-border/60 bg-white px-3 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-2 md:hidden"
        data-testid="tablet-staff-dock-mobile"
      >
        <div className="min-w-0 flex-1 rounded-lg border border-red-100 bg-red-50 px-3 py-2">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-red-800/80">Signed in as</p>
          <p className="truncate text-sm font-semibold leading-tight text-red-950">{user?.name || user?.email || 'Staff'}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="h-auto shrink-0 touch-manipulation gap-2 px-3"
          onClick={handleSwitchUser}
          data-testid="tablet-switch-user-mobile"
        >
          <LogOut className="h-4 w-4 shrink-0" />
          Log off
        </Button>
      </div>
    </div>
  );
}

function bookingShowsCreatorTag(booking) {
  const isWalkIn = booking.source === 'walk-in' && !booking.staff_only;
  const isVipHold = Boolean(booking.staff_only);
  return (isWalkIn || isVipHold) && Boolean(booking.created_by_user_name);
}

function BookingCreatorTag({ booking, compact = false }) {
  if (!bookingShowsCreatorTag(booking)) return null;
  return (
    <p
      className={cn(
        'w-full truncate rounded border border-red-100 bg-red-50 px-1.5 py-0.5 font-semibold text-red-950',
        compact ? 'mb-1 text-[9px]' : 'mb-2 text-xs'
      )}
      title={`Added by ${booking.created_by_user_name}`}
    >
      Added by {booking.created_by_user_name}
    </p>
  );
}

// Booking Card Component
function BookingCard({
  booking,
  tables,
  getStatusBadge,
  updateStatus,
  onEdit,
  compact = false,
  denseGrid = false,
  smartAssignHint,
  onSmartAssignTable
}) {
  const assignedTableNames = booking.assigned_tables
    ?.map(tid => tables.find(t => t.id === tid)?.name)
    .filter(Boolean)
    .join(', ');

  const borderLeftClass = cn(
    booking.staff_only && 'border-l-violet-600',
    !booking.staff_only && booking.status === 'seated' && 'border-l-emerald-500',
    !booking.staff_only && booking.status === 'arrived' && 'border-l-amber-500',
    !booking.staff_only && booking.status === 'confirmed' && 'border-l-blue-500',
    !booking.staff_only && booking.status === 'completed' && 'border-l-slate-400',
    !booking.staff_only && booking.status === 'cancelled' && 'border-l-red-400',
    !booking.staff_only && booking.status === 'no-show' && 'border-l-gray-400'
  );

  if (denseGrid) {
    const gridActionHint =
      booking.status === 'confirmed'
        ? 'Edit details, mark arrived, or no-show.'
        : booking.status === 'arrived'
          ? 'Edit details or mark seated.'
          : booking.status === 'seated'
            ? 'Edit details or mark completed.'
            : 'Edit booking for full options.';

    return (
      <Card
        className={cn(
          'flex h-full min-h-0 flex-col border-l-4 shadow-sm transition-all hover:shadow-md',
          borderLeftClass
        )}
        data-testid={`booking-card-${booking.id}`}
      >
        <CardContent className="flex flex-1 flex-col gap-1.5 p-2">
          <BookingCreatorTag booking={booking} compact />
          <div className="flex min-h-0 items-start justify-between gap-1">
            <h3 className="line-clamp-2 min-w-0 flex-1 text-left text-xs font-semibold leading-snug">
              {booking.customer_name}
            </h3>
            <Badge
              className={cn(
                'h-4 max-w-[5.5rem] shrink-0 truncate px-1 text-[9px] font-normal',
                getStatusBadge(booking.status)
              )}
            >
              {booking.status}
            </Badge>
          </div>
          {booking.staff_only && (
            <Badge className="h-4 w-fit border-violet-700 bg-violet-700 px-1 text-[9px] text-white hover:bg-violet-700">
              Staff
            </Badge>
          )}
          {booking.source === 'walk-in' && !booking.staff_only && (
            <Badge variant="outline" className="h-4 w-fit px-1 text-[9px]">
              Walk-in
            </Badge>
          )}
          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0 text-[10px] text-muted-foreground">
            <span className="inline-flex items-center gap-0.5">
              <Clock className="h-3 w-3 shrink-0" />
              {booking.booking_time}
            </span>
            <span className="text-border">·</span>
            <span className="inline-flex items-center gap-0.5">
              <Users className="h-3 w-3 shrink-0" />
              {booking.party_size}
            </span>
            {booking.customer_phone && (
              <a
                href={`tel:${booking.customer_phone}`}
                className="ml-auto inline-flex shrink-0 items-center gap-0.5 rounded hover:bg-muted/60 hover:text-primary"
                title={`Call ${booking.customer_phone}`}
                onClick={(e) => e.stopPropagation()}
              >
                <Phone className="h-3 w-3" />
              </a>
            )}
          </div>
          {(() => {
            const tbl = getAssignedTablesText(booking, tables);
            return (
              <div className="flex items-start gap-0.5 text-[9px] leading-tight text-foreground/90">
                <Table2 className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" />
                <span className="line-clamp-2 min-w-0 font-medium" title={Array.isArray(booking.assigned_tables) ? booking.assigned_tables.join(', ') : ''}>
                  {tbl || 'No table assigned'}
                </span>
              </div>
            );
          })()}
          <div
            className={cn(
              'rounded border px-1 py-1 text-[9px] font-semibold leading-snug',
              booking.allergies
                ? 'border-amber-400 bg-amber-100 text-amber-950'
                : 'border-border/60 bg-muted/40 text-muted-foreground'
            )}
          >
            {booking.allergies ? (
              <span className="flex items-start gap-0.5">
                <AlertTriangle className="mt-0.5 h-2.5 w-2.5 shrink-0" />
                <span className="break-words">Allergies: {booking.allergies}</span>
              </span>
            ) : (
              <span>Allergies: none noted</span>
            )}
          </div>
          {booking.special_requests && (
            <div className="mt-1 rounded border border-blue-200 bg-blue-50 px-1 py-1 text-[9px] text-blue-800 flex items-start gap-0.5">
              <MessageSquare className="mt-0.5 h-2.5 w-2.5 shrink-0" />
              <span className="break-words">{booking.special_requests}</span>
            </div>
          )}
          {(booking.deposit_status === 'paid' || booking.deposit_status === 'refunded' || isDepositUnpaid(booking)) && (
            <div className="flex flex-wrap gap-0.5">
              {booking.deposit_status === 'paid' && booking.deposit_amount > 0 && (
                <span className="inline-flex items-center gap-0.5 rounded border border-green-300/80 bg-green-50 px-1 py-px text-[9px] font-medium text-green-900">
                  <CreditCard className="h-2.5 w-2.5 shrink-0" />
                  {CURRENCY_SYMBOLS[booking.deposit_currency] || '£'}
                  {booking.deposit_amount.toFixed(2)}
                  {depositMethodTag(booking)}
                </span>
              )}
              {booking.deposit_status === 'refunded' && (
                <span className="inline-flex items-center gap-0.5 rounded border border-purple-300/80 bg-purple-50 px-1 py-px text-[9px] font-medium text-purple-900">
                  <RefreshCw className="h-2.5 w-2.5 shrink-0" />
                  Refunded
                </span>
              )}
              {isDepositUnpaid(booking) && (
                <span className="inline-flex items-center gap-0.5 rounded bg-red-600 px-1 py-px text-[9px] font-bold text-white">
                  <AlertTriangle className="h-2.5 w-2.5 shrink-0" />
                  NOT PAID
                </span>
              )}
            </div>
          )}

          <div
            className="flex items-start justify-between gap-2 border-t border-border/40 pt-1.5"
            role="group"
            aria-label="Guest quick actions"
          >
            <div className="min-w-0 flex-1 py-0.5">
              <p className="text-[10px] font-semibold leading-tight text-foreground/90">Quick actions</p>
              <p className="mt-0.5 line-clamp-2 text-[9px] leading-snug text-muted-foreground">
                {gridActionHint}
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap items-center justify-end gap-0.5">
              <Button
                size="sm"
                variant="ghost"
                className="h-7 w-7 shrink-0 p-0"
                title="Edit booking"
                onClick={onEdit}
                data-testid={`edit-booking-${booking.id}`}
              >
                <Edit2 className="h-3.5 w-3.5" />
              </Button>
              {booking.status === 'confirmed' && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 w-7 p-0"
                    title="Mark arrived"
                    onClick={() => updateStatus(booking.id, 'arrived')}
                    data-testid={`mark-arrived-${booking.id}`}
                  >
                    <UserCheck className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0 text-muted-foreground"
                    title="Mark no-show"
                    onClick={() => updateStatus(booking.id, 'no-show')}
                    data-testid={`mark-noshow-${booking.id}`}
                  >
                    <UserX className="h-3.5 w-3.5" />
                  </Button>
                </>
              )}
              {booking.status === 'arrived' && (
                <Button
                  size="sm"
                  className="h-7 w-7 bg-emerald-600 p-0 hover:bg-emerald-700"
                  title="Mark seated"
                  onClick={() => updateStatus(booking.id, 'seated')}
                  data-testid={`mark-seated-${booking.id}`}
                >
                  <Check className="h-3.5 w-3.5" />
                </Button>
              )}
              {booking.status === 'seated' && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 w-7 p-0"
                  title="Mark completed"
                  onClick={() => updateStatus(booking.id, 'completed')}
                  data-testid={`mark-completed-${booking.id}`}
                >
                  <Check className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          </div>

          {smartAssignHint != null && (
            <div className="mt-auto flex flex-col gap-1.5 border-t border-border/50 pt-1.5">
              <p className="line-clamp-2 text-[10px] leading-snug text-muted-foreground">
                <Sparkles className="mr-0.5 inline h-3 w-3 shrink-0 align-[-0.15em] text-violet-600" />
                <span className="font-medium text-foreground">Suggested:</span> {smartAssignHint}
              </p>
              {onSmartAssignTable && (
                <Button
                  type="button"
                  size="sm"
                  className="h-8 w-full touch-manipulation gap-1 bg-violet-600 px-2 text-[11px] text-white hover:bg-violet-700"
                  onClick={onSmartAssignTable}
                  data-testid={`smart-assign-table-${booking.id}`}
                >
                  <Sparkles className="h-3.5 w-3.5 shrink-0" />
                  Smart assign
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card 
      className={cn(
        "border-l-4 transition-all hover:shadow-md",
        borderLeftClass
      )}
      data-testid={`booking-card-${booking.id}`}
    >
      <CardContent className={compact ? "p-3" : "p-4"}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <BookingCreatorTag booking={booking} compact={compact} />
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <h3 className={cn("font-semibold", compact ? "text-base" : "text-lg")}>{booking.customer_name}</h3>
              <Badge className={getStatusBadge(booking.status)}>
                {booking.status}
              </Badge>
              {booking.staff_only && (
                <Badge className="border-violet-800 bg-violet-700 text-white hover:bg-violet-700">Staff hold</Badge>
              )}
              {booking.source === 'walk-in' && !booking.staff_only && (
                <Badge variant="outline">Walk-in</Badge>
              )}
              {assignedTableNames && (
                <Badge variant="secondary" className="gap-1">
                  <Table2 className="h-3 w-3" />
                  {assignedTableNames}
                </Badge>
              )}
            </div>
            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {booking.booking_time}
              </span>
              <span className="flex items-center gap-1">
                <Users className="h-4 w-4" />
                {booking.party_size} guests
              </span>
              {booking.customer_phone && (
                <a 
                  href={`tel:${booking.customer_phone}`}
                  className="flex items-center gap-1 hover:text-primary"
                >
                  <Phone className="h-4 w-4" />
                  {booking.customer_phone}
                </a>
              )}
            </div>
            {booking.allergies && (
              <div className="mt-2 p-2 bg-amber-100 rounded-lg text-sm text-amber-800 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                <span className="font-medium">Allergies:</span> {booking.allergies}
              </div>
            )}
            {booking.special_requests && (
              <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-900 flex items-start gap-2">
                <MessageSquare className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span><span className="font-medium">Special requests:</span> {booking.special_requests}</span>
              </div>
            )}
            {booking.deposit_status === 'paid' && booking.deposit_amount > 0 && (
              <div className="mt-2 p-2 bg-green-100 border border-green-300 rounded-lg text-sm text-green-800 flex items-center gap-2 font-semibold">
                <CreditCard className="h-4 w-4 flex-shrink-0" />
                <span>Deposit PAID{depositMethodTag(booking)}:</span> {CURRENCY_SYMBOLS[booking.deposit_currency] || '£'}{booking.deposit_amount.toFixed(2)}
                <span className="text-xs font-normal">(deduct from bill)</span>
              </div>
            )}
            {booking.deposit_status === 'refunded' && (
              <div className="mt-2 p-2 bg-purple-100 border border-purple-300 rounded-lg text-sm text-purple-800 flex items-center gap-2 font-semibold">
                <RefreshCw className="h-4 w-4 flex-shrink-0" />
                <span>
                  Deposit Refunded: {CURRENCY_SYMBOLS[booking.deposit_currency] || '£'}
                  {(booking.deposit_refund_amount ?? booking.deposit_amount ?? 0).toFixed(2)}
                </span>
              </div>
            )}
            {isDepositUnpaid(booking) && (
              <div className="mt-2 p-2 bg-red-600 rounded-lg text-sm text-white flex items-center gap-2 font-bold">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                <span>DEPOSIT NOT PAID ({formatDepositAmount(booking)}): do not deduct from bill</span>
              </div>
            )}
          </div>
          
          {/* Quick Actions */}
          <div className="flex gap-2 flex-wrap justify-end">
            <Button
              size="sm"
              variant="ghost"
              onClick={onEdit}
              data-testid={`edit-booking-${booking.id}`}
            >
              <Edit2 className="h-4 w-4" />
            </Button>
            {booking.status === 'confirmed' && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1"
                  onClick={() => updateStatus(booking.id, 'arrived')}
                  data-testid={`mark-arrived-${booking.id}`}
                >
                  <UserCheck className="h-4 w-4" />
                  {!compact && 'Arrived'}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="gap-1 text-muted-foreground"
                  onClick={() => updateStatus(booking.id, 'no-show')}
                  data-testid={`mark-noshow-${booking.id}`}
                >
                  <UserX className="h-4 w-4" />
                </Button>
              </>
            )}
            {booking.status === 'arrived' && (
              <Button
                size="sm"
                className="gap-1 bg-emerald-600 hover:bg-emerald-700"
                onClick={() => updateStatus(booking.id, 'seated')}
                data-testid={`mark-seated-${booking.id}`}
              >
                <Check className="h-4 w-4" />
                {!compact && 'Seat'}
              </Button>
            )}
            {booking.status === 'seated' && (
              <Button
                size="sm"
                variant="outline"
                className="gap-1"
                onClick={() => updateStatus(booking.id, 'completed')}
                data-testid={`mark-completed-${booking.id}`}
              >
                <Check className="h-4 w-4" />
                {!compact && 'Complete'}
              </Button>
            )}
          </div>
        </div>

        {smartAssignHint != null && (
          <div className="mt-3 border-t border-border/60 pt-3">
            <p className="mb-2 flex items-start gap-2 text-xs text-muted-foreground">
              <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-violet-600" />
              <span>
                <span className="font-medium text-foreground">Suggested:</span> {smartAssignHint}
              </span>
            </p>
            {onSmartAssignTable && (
              <Button
                type="button"
                size="sm"
                className="h-10 w-full touch-manipulation gap-2 bg-violet-600 text-white hover:bg-violet-700 sm:h-9"
                onClick={onSmartAssignTable}
                data-testid={`smart-assign-table-${booking.id}`}
              >
                <Sparkles className="h-4 w-4" />
                Smart assign table
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
